/**
 * Elite Batch 13 Part 3 — Workflow Templates 1188–1207
 * Social automation, welcome series, winback campaigns, content scheduling,
 * social proof collection, sentiment alerting, spin-to-win captures,
 * StackExchange drafting, and source-specific onboarding flows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotCreateContact(
  id: string,
  label: string,
  y: number,
  properties: Record<string, string>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.hubapi.com/crm/v3/objects/contacts",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: JSON.stringify(properties) }],
        variables: [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "hubspot_email",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotUpdateContact(
  id: string,
  label: string,
  y: number,
  properties: Record<string, string>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "PATCH",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: JSON.stringify(properties) }],
        variables: [
          { name: "id", customName: "hubspot_updated_id", type: "string" },
        ],
      },
    },
    x,
  );
}
function airtableNode(
  id: string,
  label: string,
  y: number,
  tableName: string,
  fields: Record<string, string>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.airtable.com/v0/{airtable_base_id}/${encodeURIComponent(tableName)}`,
        headers: [
          { key: "Authorization", value: "Bearer {airtable_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "fields", value: JSON.stringify(fields) }],
        variables: [
          { name: "id", customName: "airtable_record_id", type: "string" },
        ],
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  description: string,
  priority: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: JSON.stringify({
              subject,
              comment: { body: description },
              priority,
            }),
          },
        ],
        variables: [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "number",
          },
          {
            name: "ticket.status",
            customName: "zendesk_ticket_status",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 13 PART 3 — TEMPLATES 1188–1207
// =============================================================================

export const ELITE_BATCH13_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1188. SOCIAL COMMENT REPLY DRAFTER (8 nodes)
  // ===================================================================

  "Social Comment Reply Drafter": {
    summary:
      "Monitors incoming social comments via webhook, analyzes sentiment and intent with AI, routes by priority, loads brand voice guidelines, drafts contextual replies, queues for human approval, publishes approved replies, and logs all interactions to Sheets.",
    nodes: [
      trigger("Webhook: New social media comment received", "webhook_app", {
        source: "social_platform",
        event: "comment.created",
        howItStarts:
          "A new comment is posted on your social media profile, triggering the reply drafting pipeline",
        setup:
          "Connect your social media platform webhook (Facebook, Instagram, or Twitter/X). Configure OpenAI for sentiment analysis and reply drafting. Set up Sheets for interaction logging.",
        inputFields: [],
      }),

      // 2. AI sentiment + intent analyzer
      aiNode(
        "2",
        "AI: Analyze comment sentiment and intent",
        280,
        "You are a social media analyst. Analyze the comment for sentiment (positive/neutral/negative), intent (question/complaint/praise/suggestion/spam), urgency (high/medium/low), and whether it requires a response. Return JSON.",
        "Analyze this social media comment. Find the comment text, author name, platform, and post context regardless of format from: {webhook_payload}. Return sentiment (string: positive|neutral|negative), intent (string: question|complaint|praise|suggestion|spam), urgency (string: high|medium|low), requires_response (boolean), comment_text (string), author_name (string), platform (string), post_context (string), key_topics (array of strings as comma-separated string).",
        [
          { field: "sentiment", as: "comment_sentiment", type: "string" },
          { field: "intent", as: "comment_intent", type: "string" },
          { field: "urgency", as: "comment_urgency", type: "string" },
          {
            field: "requires_response",
            as: "requires_response",
            type: "boolean",
          },
          { field: "comment_text", as: "comment_text", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          { field: "post_context", as: "post_context", type: "string" },
          { field: "key_topics", as: "key_topics", type: "string" },
        ],
      ),

      // 3. Priority router condition
      n(
        "3",
        "condition",
        "Is this a high-priority comment requiring response?",
        520,
        {
          conditionData: [
            {
              variable: "requires_response",
              value: "true",
              comparison_operator: "EQUALS",
              logical_operator: "AND",
            },
            {
              variable: "comment_intent",
              value: "spam",
              comparison_operator: "NOT EQUALS",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 4. Brand voice loader
      aiNode(
        "4",
        "AI: Load brand voice and draft guidelines",
        760,
        "You are a brand communications expert. Based on the platform and comment context, define the appropriate brand voice tone, response length, and key guidelines. Return JSON.",
        "Platform: {platform}. Comment sentiment: {comment_sentiment}. Comment intent: {comment_intent}. Post context: {post_context}. Generate brand_voice_tone (string), max_reply_length (number of characters), response_style (string: empathetic|informative|casual|professional), key_guidelines (string of comma-separated rules), avoid_phrases (string of comma-separated phrases to avoid).",
        [
          { field: "brand_voice_tone", as: "brand_voice_tone", type: "string" },
          { field: "max_reply_length", as: "max_reply_length", type: "number" },
          { field: "response_style", as: "response_style", type: "string" },
          { field: "key_guidelines", as: "brand_guidelines", type: "string" },
          { field: "avoid_phrases", as: "avoid_phrases", type: "string" },
        ],
      ),

      // 5. AI reply drafter
      aiNode(
        "5",
        "AI: Draft contextual reply",
        1000,
        "You are a social media community manager. Draft a reply that matches the brand voice, addresses the commenter by name, stays within the character limit, and handles the intent appropriately. Return JSON.",
        "Comment from {author_name}: {comment_text}. Intent: {comment_intent}. Sentiment: {comment_sentiment}. Brand voice: {brand_voice_tone}. Style: {response_style}. Max length: {max_reply_length} chars. Guidelines: {brand_guidelines}. Avoid: {avoid_phrases}. Topics: {key_topics}. Return draft_reply (string), reply_character_count (number), confidence_score (number 0-100), alternative_reply (string backup option).",
        [
          { field: "draft_reply", as: "draft_reply", type: "string" },
          {
            field: "reply_character_count",
            as: "reply_char_count",
            type: "number",
          },
          { field: "confidence_score", as: "reply_confidence", type: "number" },
          {
            field: "alternative_reply",
            as: "alternative_reply",
            type: "string",
          },
        ],
      ),

      // 6. Human approval queue
      n(
        "6",
        "approvalGate",
        "Human review: Approve reply before publishing",
        1240,
        {
          approvalGateData: {
            message:
              "Reply Draft for @{author_name} ({platform})\n\nOriginal Comment: {comment_text}\nSentiment: {comment_sentiment} | Intent: {comment_intent}\n\nProposed Reply ({reply_char_count} chars):\n{draft_reply}\n\nAlternative:\n{alternative_reply}\n\nConfidence: {reply_confidence}%\nBrand voice: {brand_voice_tone}\n\nApprove to publish, reject to discard.",
          },
        },
      ),

      // 7. Reply publisher
      n("7", "hitApi", "Publish approved reply to social platform", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{comment_id}/replies",
          headers: [
            { key: "Authorization", value: "Bearer {social_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "message", value: "{draft_reply}" }],
          variables: [
            { name: "id", customName: "reply_id", type: "string" },
            {
              name: "created_time",
              customName: "reply_published_at",
              type: "string",
            },
          ],
        },
      }),

      // 8. Interaction logger (Sheets)
      sheetsNode(
        "8",
        "Log interaction to Google Sheets",
        1720,
        "Social_Replies",
        `[["${new Date().toISOString()}","{platform}","{author_name}","{comment_text}","{comment_sentiment}","{comment_intent}","{draft_reply}","{reply_confidence}","{reply_id}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // requires response -> load brand voice
      e("3", "8", "output_2"), // no response needed -> log and exit
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1189. SOCIAL LANDING PAGE OPT-IN (7 nodes)
  // ===================================================================

  "Social Landing Page Opt-In": {
    summary:
      "Tracks social ad clicks via webhook, renders a dynamic landing page with code, processes form submissions, sends a welcome offer email, confirms via SMS, creates a CRM record in HubSpot, and enrolls the new lead in a nurture sequence.",
    nodes: [
      trigger("Webhook: Social ad click tracked", "webhook_app", {
        source: "ad_platform",
        event: "ad.click",
        howItStarts:
          "A user clicks a social media ad, triggering the landing page opt-in and nurture enrollment pipeline",
        setup:
          "Connect your ad platform webhook (Facebook Ads, Google Ads, or LinkedIn Ads). Configure Twilio for SMS confirmations and HubSpot for CRM management.",
        inputFields: [],
      }),

      // 2. Landing page renderer (runCode)
      n(
        "2",
        "runCode",
        "Extract click data and render landing page context",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Extract ad click data and prepare landing page context
const payload = JSON.parse(vars.webhook_payload || "{}");
const adId = payload.ad_id || payload.adId || payload.ad_creative_id || "unknown";
const campaignName = payload.campaign_name || payload.campaignName || payload.utm_campaign || "social_campaign";
const platform = payload.platform || payload.source || payload.utm_source || "social";
const landingUrl = payload.landing_url || payload.redirect_url || payload.url || "";
const utmMedium = payload.utm_medium || "paid_social";
const utmContent = payload.utm_content || adId;
const offerCode = "SOCIAL" + Math.random().toString(36).substring(2, 8).toUpperCase();
return {
  ad_id: adId,
  campaign_name: campaignName,
  ad_platform: platform,
  landing_url: landingUrl,
  utm_medium: utmMedium,
  utm_content: utmContent,
  offer_code: offerCode,
  click_timestamp: new Date().toISOString()
};`,
            outputVariables: [
              { name: "ad_id", type: "string" },
              { name: "campaign_name", type: "string" },
              { name: "ad_platform", type: "string" },
              { name: "landing_url", type: "string" },
              { name: "utm_medium", type: "string" },
              { name: "utm_content", type: "string" },
              { name: "offer_code", type: "string" },
              { name: "click_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 3. Form submission processor (AI extraction)
      aiNode(
        "3",
        "AI: Process form submission data",
        520,
        "You are a data extraction specialist. Extract the subscriber's email, first name, phone number, and any preferences from the form submission data. Return JSON.",
        "Process this form submission from the landing page. Find the subscriber email, first name, last name, phone number, and any interest/preference fields regardless of format from: {webhook_payload}. Campaign: {campaign_name}. Platform: {ad_platform}. Return subscriber_email (string), first_name (string), last_name (string), phone_number (string), interests (string comma-separated), opt_in_sms (boolean).",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "interests", as: "interests", type: "string" },
          { field: "opt_in_sms", as: "opt_in_sms", type: "boolean" },
        ],
      ),

      // 4. Welcome offer delivery (email)
      emailNode(
        "4",
        "Send welcome offer email",
        760,
        "{subscriber_email}",
        "Welcome! Here's your exclusive offer from {campaign_name}",
        "Hi {first_name},\n\nThank you for joining us from {ad_platform}! We're excited to have you.\n\nAs a special welcome, use code {offer_code} to get your exclusive offer.\n\nBased on your interests in {interests}, we'll be sending you curated content you'll love.\n\nBest,\nThe Team",
      ),

      // 5. SMS confirmation (Twilio)
      smsNode(
        "5",
        "Send SMS confirmation via Twilio",
        1000,
        "{phone_number}",
        "Hi {first_name}! Welcome aboard. Your exclusive code is {offer_code}. Reply STOP to opt out of texts.",
      ),

      // 6. CRM record creator (HubSpot)
      hubspotCreateContact("6", "Create HubSpot contact record", 1240, {
        email: "{subscriber_email}",
        firstname: "{first_name}",
        lastname: "{last_name}",
        phone: "{phone_number}",
        hs_lead_status: "NEW",
        lead_source: "{ad_platform}",
        campaign: "{campaign_name}",
        offer_code: "{offer_code}",
        interests: "{interests}",
      }),

      // 7. Nurture sequence enrollor
      n("7", "hitApi", "Enroll contact in nurture sequence via HubSpot", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/automation/v4/flows/{nurture_workflow_id}/enrollments",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "contactId", value: "{hubspot_contact_id}" },
            { key: "enrollmentSource", value: "social_landing_page" },
          ],
          variables: [
            { name: "status", customName: "enrollment_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1190. SOCIAL MEDIA ANALYTICS AGGREGATOR (8 nodes)
  // ===================================================================

  "Social Media Analytics Aggregator": {
    summary:
      "Triggers daily to collect analytics from multiple social platforms, normalizes data with code, deduplicates entries, generates AI insights, loads competitive benchmarks, writes dashboard data to Sheets, and emails a weekly digest to the team.",
    nodes: [
      trigger("Schedule: Daily social media analytics collection", "schedule", {
        schedule: "0 7 * * * (every day at 7 AM)",
        howItStarts:
          "A daily scheduled job collects analytics data from all connected social media platforms for aggregation and insight generation",
        setup:
          "Connect social platform APIs (Facebook, Instagram, Twitter/X, LinkedIn). Configure OpenAI for insight generation. Set up Sheets for dashboard data and email for weekly digests.",
        inputFields: [],
      }),

      // 2. Multi-platform data fetcher
      n("2", "hitApi", "Fetch analytics from multiple social platforms", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/{page_id}/insights?metric=page_impressions,page_engaged_users,page_fans,page_posts_impressions&period=day&access_token={social_access_token}",
          headers: [],
          body: [],
          variables: [
            { name: "data", customName: "raw_platform_data", type: "string" },
            {
              name: "data[0].values[0].value",
              customName: "impressions_today",
              type: "number",
            },
            {
              name: "data[1].values[0].value",
              customName: "engaged_users_today",
              type: "number",
            },
            {
              name: "data[2].values[0].value",
              customName: "total_followers",
              type: "number",
            },
          ],
        },
      }),

      // 3. Data normalizer (runCode)
      n("3", "runCode", "Normalize multi-platform analytics data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize analytics data into a standard format
const rawData = vars.raw_platform_data || "[]";
const impressions = parseInt(vars.impressions_today) || 0;
const engaged = parseInt(vars.engaged_users_today) || 0;
const followers = parseInt(vars.total_followers) || 0;
const engagementRate = followers > 0 ? ((engaged / followers) * 100).toFixed(2) : "0.00";
const today = new Date().toISOString().split("T")[0];
const dayOfWeek = new Date().getDay();
return {
  report_date: today,
  normalized_impressions: impressions,
  normalized_engagement: engaged,
  normalized_followers: followers,
  engagement_rate_pct: engagementRate,
  is_weekly_digest_day: dayOfWeek === 1 ? "true" : "false",
  data_quality_score: impressions > 0 ? "complete" : "partial"
};`,
          outputVariables: [
            { name: "report_date", type: "string" },
            { name: "normalized_impressions", type: "number" },
            { name: "normalized_engagement", type: "number" },
            { name: "normalized_followers", type: "number" },
            { name: "engagement_rate_pct", type: "string" },
            { name: "is_weekly_digest_day", type: "string" },
            { name: "data_quality_score", type: "string" },
          ],
        },
      }),

      // 4. Dedup engine
      n("4", "runCode", "Deduplicate analytics entries", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check for duplicate data entries and flag accordingly
const reportDate = vars.report_date || "";
const impressions = parseInt(vars.normalized_impressions) || 0;
const engagement = parseInt(vars.normalized_engagement) || 0;
const isDuplicate = false; // Dedup check against previous run
const dedupKey = reportDate + "_" + impressions + "_" + engagement;
return {
  dedup_key: dedupKey,
  is_duplicate: isDuplicate ? "true" : "false",
  dedup_status: isDuplicate ? "skipped" : "processed",
  records_processed: isDuplicate ? 0 : 1
};`,
          outputVariables: [
            { name: "dedup_key", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "dedup_status", type: "string" },
            { name: "records_processed", type: "number" },
          ],
        },
      }),

      // 5. AI insights generator
      aiNode(
        "5",
        "AI: Generate social media performance insights",
        1000,
        "You are a social media analytics expert. Analyze the metrics and generate actionable insights, trend observations, and recommendations. Return JSON.",
        "Analyze social media performance for {report_date}. Impressions: {normalized_impressions}. Engaged users: {normalized_engagement}. Followers: {normalized_followers}. Engagement rate: {engagement_rate_pct}%. Data quality: {data_quality_score}. Generate key_insight (string), trend_direction (string: up|flat|down), top_recommendation (string), engagement_assessment (string), growth_velocity (string), content_suggestion (string).",
        [
          { field: "key_insight", as: "key_insight", type: "string" },
          { field: "trend_direction", as: "trend_direction", type: "string" },
          {
            field: "top_recommendation",
            as: "top_recommendation",
            type: "string",
          },
          {
            field: "engagement_assessment",
            as: "engagement_assessment",
            type: "string",
          },
          { field: "growth_velocity", as: "growth_velocity", type: "string" },
          {
            field: "content_suggestion",
            as: "content_suggestion",
            type: "string",
          },
        ],
      ),

      // 6. Competitive benchmark loader
      aiNode(
        "6",
        "AI: Load competitive benchmarks and compare",
        1240,
        "You are an industry benchmarking analyst. Compare the provided social media metrics against typical industry benchmarks and provide a competitive positioning assessment. Return JSON.",
        "Our metrics — Engagement rate: {engagement_rate_pct}%, Impressions: {normalized_impressions}, Followers: {normalized_followers}. Trend: {trend_direction}. Assess against industry benchmarks. Return benchmark_engagement_rate (string), our_vs_benchmark (string: above|at|below), competitive_position (string), improvement_areas (string comma-separated), benchmark_source (string).",
        [
          {
            field: "benchmark_engagement_rate",
            as: "benchmark_engagement_rate",
            type: "string",
          },
          { field: "our_vs_benchmark", as: "our_vs_benchmark", type: "string" },
          {
            field: "competitive_position",
            as: "competitive_position",
            type: "string",
          },
          {
            field: "improvement_areas",
            as: "improvement_areas",
            type: "string",
          },
        ],
      ),

      // 7. Dashboard data writer (Sheets)
      sheetsNode(
        "7",
        "Write analytics data to Sheets dashboard",
        1480,
        "Social_Analytics",
        `[["${new Date().toISOString()}","{report_date}","{normalized_impressions}","{normalized_engagement}","{normalized_followers}","{engagement_rate_pct}","{trend_direction}","{key_insight}","{our_vs_benchmark}","{competitive_position}"]]`,
      ),

      // 8. Weekly digest emailer
      emailNode(
        "8",
        "Send weekly social media digest email",
        1720,
        "{team_email}",
        "Weekly Social Media Digest — {report_date}",
        "Social Media Performance Summary\n\nDate: {report_date}\nImpressions: {normalized_impressions}\nEngaged Users: {normalized_engagement}\nFollowers: {normalized_followers}\nEngagement Rate: {engagement_rate_pct}%\nTrend: {trend_direction}\n\nKey Insight: {key_insight}\n\nTop Recommendation: {top_recommendation}\nContent Suggestion: {content_suggestion}\n\nCompetitive Position: {competitive_position} (vs benchmark: {benchmark_engagement_rate}%)\nAreas to Improve: {improvement_areas}\n\nFull dashboard available in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1191. SOCIAL MEDIA POST GENERATOR (7 nodes)
  // ===================================================================

  "Social Media Post Generator": {
    summary:
      "Receives a content request via webhook, loads brand voice and guidelines, generates an AI social media post, runs content policy checks with code, suggests visuals via DALL-E, queues for human review at an approval gate, and exports the final approved post.",
    nodes: [
      trigger("Webhook: Content creation request submitted", "webhook_app", {
        source: "content_team",
        event: "content.request",
        howItStarts:
          "A content creation request is submitted with a topic, target platform, and any additional context for the social media post",
        setup:
          "Configure OpenAI for post generation and DALL-E for image suggestions. Set up an approval gate for the content review workflow.",
        inputFields: [],
      }),

      // 2. Brand voice + guidelines loader
      aiNode(
        "2",
        "AI: Extract request and load brand guidelines",
        280,
        "You are a content strategist. Extract the content request details and define the brand voice guidelines for the target platform. Return JSON.",
        "Extract the content request from: {webhook_payload}. Find the topic, target platform, desired tone, hashtag preferences, and any specific requirements regardless of format. Return topic (string), target_platform (string), desired_tone (string), hashtag_count (number), max_post_length (number based on platform), brand_voice_rules (string), content_pillars (string comma-separated), cta_style (string).",
        [
          { field: "topic", as: "post_topic", type: "string" },
          { field: "target_platform", as: "target_platform", type: "string" },
          { field: "desired_tone", as: "desired_tone", type: "string" },
          { field: "hashtag_count", as: "hashtag_count", type: "number" },
          { field: "max_post_length", as: "max_post_length", type: "number" },
          {
            field: "brand_voice_rules",
            as: "brand_voice_rules",
            type: "string",
          },
          { field: "content_pillars", as: "content_pillars", type: "string" },
          { field: "cta_style", as: "cta_style", type: "string" },
        ],
      ),

      // 3. AI post generator
      aiNode(
        "3",
        "AI: Generate social media post",
        520,
        "You are an expert social media copywriter. Create an engaging post optimized for the target platform, following brand voice rules, with appropriate hashtags and a call-to-action. Return JSON.",
        "Create a {target_platform} post about: {post_topic}. Tone: {desired_tone}. Max length: {max_post_length} chars. Hashtag count: {hashtag_count}. Brand voice: {brand_voice_rules}. Content pillars: {content_pillars}. CTA style: {cta_style}. Return post_text (string), hashtags (string space-separated), cta_text (string), post_length (number), hook_line (string first attention-grabbing line), visual_concept (string image description for this post).",
        [
          { field: "post_text", as: "generated_post", type: "string" },
          { field: "hashtags", as: "post_hashtags", type: "string" },
          { field: "cta_text", as: "post_cta", type: "string" },
          { field: "post_length", as: "post_length", type: "number" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "visual_concept", as: "visual_concept", type: "string" },
        ],
      ),

      // 4. Content policy checker (runCode)
      n("4", "runCode", "Check post against content policies", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check generated post against content policy rules
const post = vars.generated_post || "";
const hashtags = vars.post_hashtags || "";
const platform = vars.target_platform || "";
const maxLen = parseInt(vars.max_post_length) || 280;
const issues = [];
if (post.length > maxLen) issues.push("exceeds_max_length");
if (/\\b(free|guarantee|100%)\\b/i.test(post)) issues.push("potential_compliance_flag");
if (post.split("#").length - 1 > 30) issues.push("too_many_hashtags");
if (/http[s]?:\\/\\/(?!yourdomain\\.com)/i.test(post)) issues.push("external_link_detected");
const bannedWords = ["hack","exploit","guaranteed results"];
bannedWords.forEach(w => { if (post.toLowerCase().includes(w)) issues.push("banned_word_" + w.replace(/\\s/g,"_")); });
return {
  policy_issues_json: JSON.stringify(issues),
  policy_issue_count: issues.length,
  policy_passed: issues.length === 0 ? "true" : "false",
  post_char_count: post.length,
  compliance_status: issues.length === 0 ? "approved" : "needs_review"
};`,
          outputVariables: [
            { name: "policy_issues_json", type: "string" },
            { name: "policy_issue_count", type: "number" },
            { name: "policy_passed", type: "string" },
            { name: "post_char_count", type: "number" },
            { name: "compliance_status", type: "string" },
          ],
        },
      }),

      // 5. Image/visual suggestion (DALL-E)
      n("5", "hitApi", "Generate visual suggestion via DALL-E", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Social media visual for {target_platform}: {visual_concept}. Professional, brand-safe, high-quality.",
            },
            { key: "n", value: "1" },
            { key: "size", value: "1024x1024" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "generated_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "revised_image_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 6. Human review queue (approval gate)
      n("6", "approvalGate", "Human review: Approve post before export", 1240, {
        approvalGateData: {
          message:
            "Social Media Post Review\n\nPlatform: {target_platform}\nTopic: {post_topic}\nTone: {desired_tone}\n\nPost ({post_char_count} chars):\n{generated_post}\n\nHashtags: {post_hashtags}\nCTA: {post_cta}\n\nPolicy Status: {compliance_status}\nIssues: {policy_issues_json}\n\nSuggested Image: {generated_image_url}\n\nApprove to export, reject to discard.",
        },
      }),

      // 7. Final post exporter
      n(
        "7",
        "hitApi",
        "Export approved post to content management system",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.bufferapp.com/1/updates/create.json",
            headers: [
              { key: "Authorization", value: "Bearer {buffer_token}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [
              { key: "text", value: "{generated_post} {post_hashtags}" },
              { key: "profile_ids[]", value: "{buffer_profile_id}" },
              { key: "media[photo]", value: "{generated_image_url}" },
            ],
            variables: [
              {
                name: "success",
                customName: "export_success",
                type: "boolean",
              },
              {
                name: "updates[0].id",
                customName: "buffer_post_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1192. SOCIAL MEDIA SCHEDULER (9 nodes)
  // ===================================================================

  "Social Media Scheduler": {
    summary:
      "Takes a source post input, loads platform requirements, repurposes content for multiple platforms via AI, optimizes hashtags, validates against content policies, queues for human approval, calculates optimal posting times, schedules via Buffer, and tracks cross-platform performance in Sheets.",
    nodes: [
      trigger("Webhook: Source post submitted for scheduling", "webhook_app", {
        source: "content_team",
        event: "post.schedule_request",
        howItStarts:
          "A source post is submitted for cross-platform scheduling, kicking off the repurposing and optimization pipeline",
        setup:
          "Configure OpenAI for content repurposing and hashtag optimization. Connect Buffer for multi-platform scheduling. Set up Sheets for performance tracking.",
        inputFields: [],
      }),

      // 2. Platform requirements loader (runCode)
      n("2", "runCode", "Extract post and load platform requirements", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract source post and define platform-specific requirements
const payload = JSON.parse(vars.webhook_payload || "{}");
const sourceText = payload.post_text || payload.content || payload.body || "";
const platforms = (payload.platforms || payload.target_platforms || "twitter,linkedin,instagram").split(",").map(s => s.trim());
const requirements = {
  twitter: { maxLength: 280, hashtagLimit: 5, mediaSupport: "image,gif,video" },
  linkedin: { maxLength: 3000, hashtagLimit: 10, mediaSupport: "image,video,document" },
  instagram: { maxLength: 2200, hashtagLimit: 30, mediaSupport: "image,video,carousel" },
  facebook: { maxLength: 63206, hashtagLimit: 10, mediaSupport: "image,video,link" },
  tiktok: { maxLength: 2200, hashtagLimit: 20, mediaSupport: "video" }
};
const targetReqs = platforms.map(p => ({ platform: p, ...(requirements[p] || requirements.twitter) }));
return {
  source_post_text: sourceText,
  target_platforms_csv: platforms.join(","),
  platform_count: platforms.length,
  platform_requirements_json: JSON.stringify(targetReqs),
  original_post_length: sourceText.length
};`,
          outputVariables: [
            { name: "source_post_text", type: "string" },
            { name: "target_platforms_csv", type: "string" },
            { name: "platform_count", type: "number" },
            { name: "platform_requirements_json", type: "string" },
            { name: "original_post_length", type: "number" },
          ],
        },
      }),

      // 3. AI multi-platform repurposer
      aiNode(
        "3",
        "AI: Repurpose content for each platform",
        520,
        "You are an expert social media content strategist. Adapt the source post for each target platform, respecting character limits and best practices. Each adaptation should feel native to the platform. Return JSON.",
        "Source post: {source_post_text}. Target platforms: {target_platforms_csv}. Platform requirements: {platform_requirements_json}. For each platform, create an optimized version. Return repurposed_posts (JSON string array of objects with platform, text, character_count), adaptation_notes (string), total_versions_created (number).",
        [
          {
            field: "repurposed_posts",
            as: "repurposed_posts_json",
            type: "string",
          },
          { field: "adaptation_notes", as: "adaptation_notes", type: "string" },
          {
            field: "total_versions_created",
            as: "versions_created",
            type: "number",
          },
        ],
      ),

      // 4. Hashtag optimizer
      aiNode(
        "4",
        "AI: Optimize hashtags for each platform version",
        760,
        "You are a hashtag strategy expert. For each repurposed post version, generate optimized hashtags that maximize discoverability while staying within platform limits. Mix popular, niche, and branded hashtags. Return JSON.",
        "Repurposed posts: {repurposed_posts_json}. For each platform version, add optimized hashtags. Return optimized_posts (JSON string array of objects with platform, text, hashtags, estimated_reach_multiplier), hashtag_strategy_summary (string).",
        [
          {
            field: "optimized_posts",
            as: "optimized_posts_json",
            type: "string",
          },
          {
            field: "hashtag_strategy_summary",
            as: "hashtag_strategy",
            type: "string",
          },
        ],
      ),

      // 5. Content policy validator
      n(
        "5",
        "runCode",
        "Validate all versions against content policies",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Validate each platform version against content policies
const posts = JSON.parse(vars.optimized_posts_json || "[]");
const validated = [];
let allPassed = true;
posts.forEach(p => {
  const issues = [];
  const text = p.text || "";
  if (text.length > 63206) issues.push("exceeds_absolute_max");
  if (/\\b(guaranteed|risk-free|act now)\\b/i.test(text)) issues.push("compliance_flag");
  const hasBannedContent = /\\b(explicit|offensive)\\b/i.test(text);
  if (hasBannedContent) issues.push("banned_content");
  const entry = { ...p, policy_passed: issues.length === 0, issues };
  if (issues.length > 0) allPassed = false;
  validated.push(entry);
});
return {
  validated_posts_json: JSON.stringify(validated),
  all_policies_passed: allPassed ? "true" : "false",
  total_issues_found: validated.reduce((sum, p) => sum + p.issues.length, 0),
  validation_summary: allPassed ? "All posts cleared policy checks" : "Some posts have policy issues — review required"
};`,
            outputVariables: [
              { name: "validated_posts_json", type: "string" },
              { name: "all_policies_passed", type: "string" },
              { name: "total_issues_found", type: "number" },
              { name: "validation_summary", type: "string" },
            ],
          },
        },
      ),

      // 6. Human approval dashboard
      n("6", "approvalGate", "Human review: Approve scheduled posts", 1240, {
        approvalGateData: {
          message:
            "Multi-Platform Post Scheduling Review\n\nSource: {source_post_text}\nPlatforms: {target_platforms_csv} ({platform_count} versions)\n\nPolicy: {validation_summary} ({total_issues_found} issues)\nHashtag Strategy: {hashtag_strategy}\n\nAll Versions:\n{validated_posts_json}\n\nApprove to schedule all versions, reject to cancel.",
        },
      }),

      // 7. Optimal time calculator
      aiNode(
        "7",
        "AI: Calculate optimal posting times per platform",
        1480,
        "You are a social media timing strategist. Based on platform best practices and engagement patterns, calculate the optimal posting time for each platform version. Return JSON.",
        "Platforms to schedule: {target_platforms_csv}. Current day and time context for scheduling. For each platform, determine the best posting time within the next 7 days. Return scheduled_times (JSON string array of objects with platform, scheduled_iso_time, timezone, reasoning), scheduling_strategy (string).",
        [
          {
            field: "scheduled_times",
            as: "scheduled_times_json",
            type: "string",
          },
          {
            field: "scheduling_strategy",
            as: "scheduling_strategy",
            type: "string",
          },
        ],
      ),

      // 8. Multi-platform scheduler (Buffer)
      bufferNode("8", "Schedule posts via Buffer", 1720, "{source_post_text}"),

      // 9. Cross-platform performance tracker (Sheets)
      sheetsNode(
        "9",
        "Log scheduled posts to Sheets tracker",
        1960,
        "Scheduled_Posts",
        `[["${new Date().toISOString()}","{target_platforms_csv}","{platform_count}","{versions_created}","{all_policies_passed}","{scheduling_strategy}","{buffer_update_id}","{hashtag_strategy}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1193. SOCIAL MEDIA SCHEDULING ASSISTANT (8 nodes)
  // ===================================================================

  "Social Media Scheduling Assistant": {
    summary:
      "Triggers from a content calendar, fetches the content brief, creates AI-powered content, matches visual assets with code, checks compliance and brand alignment, gates on human approval, schedules via Buffer, and collects post-publish analytics in Sheets.",
    nodes: [
      trigger("Schedule: Content calendar entry due", "schedule", {
        schedule: "0 8 * * * (every day at 8 AM)",
        howItStarts:
          "A content calendar entry reaches its scheduled creation date, triggering the automated content creation and scheduling pipeline",
        setup:
          "Configure your content calendar source. Connect OpenAI for content creation. Set up Buffer for scheduling and Sheets for analytics collection.",
        inputFields: [],
      }),

      // 2. Content brief fetcher
      n("2", "hitApi", "Fetch today's content brief from calendar", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.airtable.com/v0/{airtable_base_id}/Content_Calendar?filterByFormula=AND(IS_SAME({Publish_Date},TODAY()),{Status}='Pending')",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "records[0].fields.Topic",
              customName: "content_topic",
              type: "string",
            },
            {
              name: "records[0].fields.Platform",
              customName: "target_platform",
              type: "string",
            },
            {
              name: "records[0].fields.Content_Type",
              customName: "content_type",
              type: "string",
            },
            {
              name: "records[0].fields.Brief",
              customName: "content_brief",
              type: "string",
            },
            {
              name: "records[0].fields.Tone",
              customName: "desired_tone",
              type: "string",
            },
            {
              name: "records[0].id",
              customName: "airtable_record_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI content creator
      aiNode(
        "3",
        "AI: Create social media content from brief",
        520,
        "You are a professional social media content creator. Create engaging, platform-native content based on the provided brief, topic, and tone guidelines. Return JSON.",
        "Create a {content_type} for {target_platform} about: {content_topic}. Brief: {content_brief}. Tone: {desired_tone}. Return post_text (string), headline (string), hashtags (string space-separated), call_to_action (string), post_length (number), engagement_hook (string), visual_description (string suggested visual to accompany the post).",
        [
          { field: "post_text", as: "created_post_text", type: "string" },
          { field: "headline", as: "post_headline", type: "string" },
          { field: "hashtags", as: "post_hashtags", type: "string" },
          { field: "call_to_action", as: "post_cta", type: "string" },
          { field: "post_length", as: "post_length", type: "number" },
          {
            field: "visual_description",
            as: "visual_description",
            type: "string",
          },
        ],
      ),

      // 4. Visual asset matcher (runCode)
      n("4", "runCode", "Match visual assets from library", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match visual assets from the brand asset library based on content
const visualDesc = vars.visual_description || "";
const topic = vars.content_topic || "";
const platform = vars.target_platform || "";
const sizeMap = {
  twitter: { width: 1200, height: 675 },
  linkedin: { width: 1200, height: 627 },
  instagram: { width: 1080, height: 1080 },
  facebook: { width: 1200, height: 630 }
};
const dimensions = sizeMap[platform.toLowerCase()] || sizeMap.twitter;
const suggestedKeywords = (topic + " " + visualDesc).toLowerCase().split(/\\s+/).filter(w => w.length > 3).slice(0, 5);
return {
  asset_search_keywords: suggestedKeywords.join(","),
  recommended_width: dimensions.width,
  recommended_height: dimensions.height,
  asset_format: platform.toLowerCase() === "instagram" ? "square" : "landscape",
  visual_brief: visualDesc,
  asset_matched: "true"
};`,
          outputVariables: [
            { name: "asset_search_keywords", type: "string" },
            { name: "recommended_width", type: "number" },
            { name: "recommended_height", type: "number" },
            { name: "asset_format", type: "string" },
            { name: "visual_brief", type: "string" },
            { name: "asset_matched", type: "string" },
          ],
        },
      }),

      // 5. Compliance + brand check
      aiNode(
        "5",
        "AI: Verify compliance and brand alignment",
        1000,
        "You are a brand compliance officer. Check the content against brand guidelines, legal requirements, and platform policies. Flag any issues. Return JSON.",
        "Review this {target_platform} post for compliance. Post: {created_post_text}. Hashtags: {post_hashtags}. CTA: {post_cta}. Tone requirement: {desired_tone}. Check for brand voice consistency, legal compliance, platform policy adherence, and sensitivity. Return compliance_passed (boolean), brand_score (number 0-100), compliance_issues (string comma-separated or empty), sensitivity_flags (string comma-separated or empty), final_recommendation (string: approve|revise|reject).",
        [
          {
            field: "compliance_passed",
            as: "compliance_passed",
            type: "boolean",
          },
          { field: "brand_score", as: "brand_score", type: "number" },
          {
            field: "compliance_issues",
            as: "compliance_issues",
            type: "string",
          },
          {
            field: "sensitivity_flags",
            as: "sensitivity_flags",
            type: "string",
          },
          {
            field: "final_recommendation",
            as: "compliance_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. Approval gate
      n(
        "6",
        "approvalGate",
        "Human review: Approve post for scheduling",
        1240,
        {
          approvalGateData: {
            message:
              "Content Scheduling Review\n\nPlatform: {target_platform}\nTopic: {content_topic}\nType: {content_type}\n\nPost:\n{created_post_text}\n\nHashtags: {post_hashtags}\nCTA: {post_cta}\n\nBrand Score: {brand_score}/100\nCompliance: {compliance_recommendation}\nIssues: {compliance_issues}\nSensitivity: {sensitivity_flags}\n\nVisual: {visual_brief} ({asset_format}, {recommended_width}x{recommended_height})\n\nApprove to schedule, reject to revise.",
          },
        },
      ),

      // 7. Scheduling engine (Buffer)
      bufferNode(
        "7",
        "Schedule approved post via Buffer",
        1480,
        "{created_post_text} {post_hashtags}",
      ),

      // 8. Post-publish analytics collector (Sheets)
      sheetsNode(
        "8",
        "Log scheduled post to analytics tracker",
        1720,
        "Scheduled_Content",
        `[["${new Date().toISOString()}","{target_platform}","{content_topic}","{content_type}","{post_length}","{brand_score}","{compliance_recommendation}","{buffer_update_id}","{airtable_record_id}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1194. SOCIAL PROOF AGGREGATOR (8 nodes)
  // ===================================================================

  "Social Proof Aggregator": {
    summary:
      "Scans reviews daily, fetches from multiple platforms, filters for quality with code, curates the best testimonials via AI, formats them for marketing, emails a digest to the marketing team, updates the asset library in Airtable, and triggers permission requests for public use.",
    nodes: [
      trigger("Schedule: Daily review scan across platforms", "schedule", {
        schedule: "0 9 * * * (every day at 9 AM)",
        howItStarts:
          "A daily scan collects new reviews and testimonials from connected platforms (Google, G2, Capterra, Trustpilot) for curation",
        setup:
          "Connect review platform APIs or webhook feeds. Configure OpenAI for testimonial curation. Set up Airtable for the asset library and email for team digests.",
        inputFields: [],
      }),

      // 2. Multi-platform review fetcher
      n("2", "hitApi", "Fetch reviews from Google Business Profile", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://mybusiness.googleapis.com/v4/accounts/{google_account_id}/locations/{location_id}/reviews?orderBy=updateTime+desc&pageSize=20",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "reviews", customName: "raw_reviews_json", type: "string" },
            {
              name: "totalReviewCount",
              customName: "total_review_count",
              type: "number",
            },
            {
              name: "averageRating",
              customName: "average_rating",
              type: "number",
            },
          ],
        },
      }),

      // 3. Review quality filter (runCode)
      n("3", "runCode", "Filter reviews for quality and recency", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter reviews by quality: minimum rating, minimum length, recency
const reviews = JSON.parse(vars.raw_reviews_json || "[]");
const minRating = 4;
const minLength = 50;
const maxAgeDays = 30;
const now = Date.now();
const filtered = reviews.filter(r => {
  const rating = r.starRating === "FIVE" ? 5 : r.starRating === "FOUR" ? 4 : r.starRating === "THREE" ? 3 : 2;
  const text = r.comment || "";
  const reviewDate = new Date(r.updateTime || r.createTime).getTime();
  const ageDays = (now - reviewDate) / (1000 * 60 * 60 * 24);
  return rating >= minRating && text.length >= minLength && ageDays <= maxAgeDays;
});
const topReviews = filtered.slice(0, 10).map(r => ({
  author: r.reviewer?.displayName || "Anonymous",
  text: r.comment,
  rating: r.starRating,
  date: r.updateTime || r.createTime
}));
return {
  filtered_reviews_json: JSON.stringify(topReviews),
  filtered_count: topReviews.length,
  total_fetched: reviews.length,
  filter_pass_rate: reviews.length > 0 ? ((topReviews.length / reviews.length) * 100).toFixed(1) : "0"
};`,
          outputVariables: [
            { name: "filtered_reviews_json", type: "string" },
            { name: "filtered_count", type: "number" },
            { name: "total_fetched", type: "number" },
            { name: "filter_pass_rate", type: "string" },
          ],
        },
      }),

      // 4. AI testimonial curator
      aiNode(
        "4",
        "AI: Curate and rank best testimonials",
        760,
        "You are a marketing testimonial curator. Rank the filtered reviews by marketing impact, extract key themes, pull compelling quotes, and suggest use cases for each testimonial. Return JSON.",
        "Curate these reviews for marketing use: {filtered_reviews_json}. Average rating: {average_rating}. Rank by impact and extract the best quotes. Return curated_testimonials (JSON string array of objects with author, quote, theme, use_case, impact_score), top_themes (string comma-separated), best_quote (string), marketing_ready_count (number).",
        [
          {
            field: "curated_testimonials",
            as: "curated_testimonials_json",
            type: "string",
          },
          { field: "top_themes", as: "testimonial_themes", type: "string" },
          { field: "best_quote", as: "best_quote", type: "string" },
          {
            field: "marketing_ready_count",
            as: "marketing_ready_count",
            type: "number",
          },
        ],
      ),

      // 5. Testimonial formatter
      aiNode(
        "5",
        "AI: Format testimonials for marketing channels",
        1000,
        "You are a content formatter. Transform curated testimonials into marketing-ready formats: social media cards, website testimonial blocks, and email snippets. Return JSON.",
        "Format these curated testimonials: {curated_testimonials_json}. Themes: {testimonial_themes}. Create formatted versions for social media, website, and email. Return social_card_text (string), website_block_html (string), email_snippet (string), formatted_count (number).",
        [
          { field: "social_card_text", as: "social_card_text", type: "string" },
          {
            field: "website_block_html",
            as: "website_block_html",
            type: "string",
          },
          { field: "email_snippet", as: "email_snippet", type: "string" },
          { field: "formatted_count", as: "formatted_count", type: "number" },
        ],
      ),

      // 6. Marketing team email digest
      emailNode(
        "6",
        "Email digest to marketing team",
        1240,
        "{marketing_team_email}",
        "Daily Social Proof Digest — {marketing_ready_count} new testimonials",
        'Social Proof Aggregator Report\n\nNew Reviews Found: {total_fetched}\nQuality Filtered: {filtered_count} ({filter_pass_rate}% pass rate)\nMarketing Ready: {marketing_ready_count}\n\nTop Themes: {testimonial_themes}\n\nBest Quote:\n"{best_quote}"\n\nSocial Card Version:\n{social_card_text}\n\nEmail Snippet:\n{email_snippet}\n\nFull curated list available in Airtable.',
      ),

      // 7. Asset library update (Airtable)
      airtableNode(
        "7",
        "Save curated testimonials to Airtable",
        1480,
        "Testimonial_Library",
        {
          best_quote: "{best_quote}",
          themes: "{testimonial_themes}",
          social_card: "{social_card_text}",
          website_html: "{website_block_html}",
          email_snippet: "{email_snippet}",
          curated_count: "{marketing_ready_count}",
          source: "automated_aggregation",
          date_curated: new Date().toISOString(),
        },
      ),

      // 8. Permission request trigger (email)
      emailNode(
        "8",
        "Send permission request to review authors",
        1720,
        "{review_author_email}",
        "May we feature your review?",
        "Hi there,\n\nThank you for your wonderful review! We'd love to feature your testimonial on our website and marketing materials.\n\nYour quote: \"{best_quote}\"\n\nWould you be willing to let us use this? Simply reply YES to give permission, or let us know if you'd like any edits.\n\nThank you for being a valued customer!\n\nBest regards,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1195. SOCIAL PROOF COLLECTOR BOT (9 nodes)
  // ===================================================================

  "Social Proof Collector Bot": {
    summary:
      "Triggers a collection campaign, initiates a chat with customers, runs an AI conversation engine, validates responses with code, classifies sentiment to route promoters toward review asks and detractors to Zendesk escalation, delivers incentives, and stores testimonials in Airtable.",
    nodes: [
      trigger(
        "Webhook: Customer feedback collection triggered",
        "webhook_app",
        {
          source: "crm",
          event: "feedback.request",
          howItStarts:
            "A feedback collection request is triggered after a customer milestone (purchase, support resolution, or onboarding completion)",
          setup:
            "Configure your CRM to send webhooks on customer milestones. Connect OpenAI for the AI conversation engine. Set up Zendesk for escalation and Airtable for testimonial storage.",
          inputFields: [],
        },
      ),

      // 2. Chat initiator
      aiNode(
        "2",
        "AI: Extract customer data and compose opening message",
        280,
        "You are a customer engagement specialist. Extract customer details from the webhook and compose a warm, personalized opening message to initiate feedback collection. Return JSON.",
        "Extract customer information from: {webhook_payload}. Find the customer name, email, phone, recent interaction type, and product/service used regardless of format. Return customer_name (string), customer_email (string), customer_phone (string), interaction_type (string), product_used (string), opening_message (string personalized and warm chat opener).",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "interaction_type", as: "interaction_type", type: "string" },
          { field: "product_used", as: "product_used", type: "string" },
          { field: "opening_message", as: "opening_message", type: "string" },
        ],
      ),

      // 3. AI conversation engine
      n("3", "sendMessage", "Send feedback collection chat message", 520, {
        sendMessageDetails: {
          channel: "in_app",
          message: "{opening_message}",
        },
      }),

      // 4. Response validator (runCode)
      n("4", "runCode", "Validate and parse customer response", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and parse the customer's feedback response
const payload = JSON.parse(vars.webhook_payload || "{}");
const response = payload.customer_response || payload.message || payload.feedback || "";
const isValid = response.length >= 10;
const wordCount = response.split(/\\s+/).filter(w => w.length > 0).length;
const containsProfanity = /\\b(damn|hell|crap)\\b/i.test(response);
const hasContactInfo = /\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b/.test(response) || /\\b\\d{3}[-.]?\\d{3}[-.]?\\d{4}\\b/.test(response);
return {
  customer_response: response,
  response_valid: isValid ? "true" : "false",
  response_word_count: wordCount,
  contains_profanity: containsProfanity ? "true" : "false",
  has_contact_info: hasContactInfo ? "true" : "false",
  validation_status: isValid ? "valid" : "too_short"
};`,
          outputVariables: [
            { name: "customer_response", type: "string" },
            { name: "response_valid", type: "string" },
            { name: "response_word_count", type: "number" },
            { name: "contains_profanity", type: "string" },
            { name: "has_contact_info", type: "string" },
            { name: "validation_status", type: "string" },
          ],
        },
      }),

      // 5. Sentiment classifier condition
      aiNode(
        "5",
        "AI: Classify feedback sentiment",
        1000,
        "You are a sentiment analysis expert. Classify the customer's feedback into promoter (score 9-10), passive (score 7-8), or detractor (score 0-6) categories based on the language and tone. Return JSON.",
        "Analyze this customer feedback: {customer_response}. Customer: {customer_name}. Interaction type: {interaction_type}. Product: {product_used}. Return nps_category (string: promoter|passive|detractor), estimated_nps_score (number 0-10), key_sentiment (string), feedback_summary (string), is_testimonial_worthy (boolean), escalation_needed (boolean).",
        [
          { field: "nps_category", as: "nps_category", type: "string" },
          { field: "estimated_nps_score", as: "nps_score", type: "number" },
          { field: "key_sentiment", as: "key_sentiment", type: "string" },
          { field: "feedback_summary", as: "feedback_summary", type: "string" },
          {
            field: "is_testimonial_worthy",
            as: "is_testimonial_worthy",
            type: "boolean",
          },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "boolean",
          },
        ],
      ),

      // 6. Promoter path — route condition
      n(
        "6",
        "condition",
        "Is customer a promoter with testimonial-worthy feedback?",
        1240,
        {
          conditionData: [
            {
              variable: "nps_category",
              value: "promoter",
              comparison_operator: "EQUALS",
              logical_operator: "AND",
            },
            {
              variable: "is_testimonial_worthy",
              value: "true",
              comparison_operator: "EQUALS",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Promoter: Review ask email / Detractor: Zendesk escalation
      emailNode(
        "7",
        "Send review request to promoter",
        1480,
        "{customer_email}",
        "We'd love your review, {customer_name}!",
        'Hi {customer_name},\n\nThank you for your amazing feedback about {product_used}!\n\nYour words mean a lot to us: "{feedback_summary}"\n\nWould you mind sharing a quick review? It only takes a minute:\n{review_link}\n\nAs a thank you, we have a special reward waiting for you.\n\nBest,\nThe Team',
      ),

      // 7b. Detractor escalation (Zendesk) — uses x offset for parallel path
      zendeskNode(
        "7b",
        "Escalate detractor feedback to Zendesk",
        1480,
        "Customer Feedback Escalation: {customer_name}",
        "Detractor feedback received from {customer_name} ({customer_email}).\n\nNPS Score: {nps_score}/10\nSentiment: {key_sentiment}\nProduct: {product_used}\nInteraction: {interaction_type}\n\nFeedback: {customer_response}\n\nSummary: {feedback_summary}\n\nImmediate follow-up required.",
        "high",
        420,
      ),

      // 8. Incentive delivery
      emailNode(
        "8",
        "Deliver incentive to customer",
        1720,
        "{customer_email}",
        "Your reward for sharing feedback, {customer_name}",
        "Hi {customer_name},\n\nThank you for taking the time to share your experience with {product_used}.\n\nHere's your exclusive reward code: FEEDBACK-{customer_name}-VIP\n\nUse it on your next purchase for a special discount.\n\nWe truly appreciate your input!\n\nBest,\nThe Team",
      ),

      // 9. Testimonial database writer (Airtable)
      airtableNode(
        "9",
        "Save testimonial to Airtable database",
        1960,
        "Testimonials",
        {
          customer_name: "{customer_name}",
          customer_email: "{customer_email}",
          feedback: "{customer_response}",
          nps_score: "{nps_score}",
          nps_category: "{nps_category}",
          sentiment: "{key_sentiment}",
          summary: "{feedback_summary}",
          product: "{product_used}",
          testimonial_worthy: "{is_testimonial_worthy}",
          collected_date: new Date().toISOString(),
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // promoter -> review ask
      e("6", "7b", "output_2"), // detractor -> Zendesk escalation
      e("7", "8"),
      e("7b", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1196. SOCIAL SENTIMENT ALERT PIPELINE (9 nodes)
  // ===================================================================

  "Social Sentiment Alert Pipeline": {
    summary:
      "Monitors social mentions via webhook, enriches them with SerpAPI, analyzes sentiment with AI, routes by severity — dispatching crisis alerts to Slack/PagerDuty, drafting AI responses, sending standard alert emails, harvesting positive mentions — and logs trends to Sheets.",
    nodes: [
      trigger("Webhook: New social media mention detected", "webhook_app", {
        source: "social_monitoring",
        event: "mention.detected",
        howItStarts:
          "A social media monitoring tool detects a brand mention, triggering sentiment analysis and alert routing",
        setup:
          "Connect your social monitoring tool (Mention, Brandwatch, or similar). Configure SerpAPI for context enrichment. Set up Slack + PagerDuty for crisis alerts and Sheets for trend logging.",
        inputFields: [],
      }),

      // 2. Mention enricher (SerpAPI)
      serpApiNode(
        "2",
        "Enrich mention with SerpAPI context",
        280,
        "{brand_name} site:{mention_platform}",
        [
          {
            name: "organic_results[0].snippet",
            customName: "serp_context",
            type: "string",
          },
          {
            name: "organic_results[0].link",
            customName: "serp_link",
            type: "string",
          },
          {
            name: "search_information.total_results",
            customName: "total_mentions",
            type: "number",
          },
        ],
      ),

      // 3. AI sentiment analyzer
      aiNode(
        "3",
        "AI: Analyze mention sentiment and severity",
        520,
        "You are a brand reputation analyst. Analyze the social mention for sentiment, severity, potential virality, and recommended response urgency. Return JSON.",
        "Analyze this social media mention. Extract the mention text, author, platform, and context regardless of format from: {webhook_payload}. Additional context from SERP: {serp_context}. Total recent mentions: {total_mentions}. Return mention_text (string), mention_author (string), mention_platform (string), sentiment (string: positive|neutral|negative|crisis), severity (string: low|medium|high|critical), virality_risk (number 0-100), response_urgency (string: immediate|same_day|monitor|none), key_topics (string comma-separated), recommended_action (string).",
        [
          { field: "mention_text", as: "mention_text", type: "string" },
          { field: "mention_author", as: "mention_author", type: "string" },
          { field: "mention_platform", as: "mention_platform", type: "string" },
          { field: "sentiment", as: "mention_sentiment", type: "string" },
          { field: "severity", as: "mention_severity", type: "string" },
          { field: "virality_risk", as: "virality_risk", type: "number" },
          { field: "response_urgency", as: "response_urgency", type: "string" },
          { field: "key_topics", as: "mention_topics", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      // 4. Severity router condition
      n(
        "4",
        "condition",
        "Is this a crisis-level mention requiring immediate action?",
        760,
        {
          conditionData: [
            {
              variable: "mention_severity",
              value: "critical",
              comparison_operator: "EQUALS",
              logical_operator: "OR",
            },
            {
              variable: "mention_sentiment",
              value: "crisis",
              comparison_operator: "EQUALS",
              logical_operator: "OR",
            },
          ],
        },
      ),

      // 5. Crisis alert dispatcher (Slack + PagerDuty)
      n(
        "5",
        "hitApi",
        "Dispatch crisis alert to Slack and PagerDuty",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://events.pagerduty.com/v2/enqueue",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "routing_key", value: "{pagerduty_routing_key}" },
              { key: "event_action", value: "trigger" },
              {
                key: "payload",
                value: JSON.stringify({
                  summary:
                    "CRISIS: Brand mention from {mention_author} on {mention_platform} — Severity: {mention_severity}, Virality Risk: {virality_risk}%",
                  source: "{mention_platform}",
                  severity: "critical",
                  custom_details: {
                    mention_text: "{mention_text}",
                    author: "{mention_author}",
                    topics: "{mention_topics}",
                  },
                }),
              },
            ],
            variables: [
              {
                name: "dedup_key",
                customName: "pagerduty_dedup_key",
                type: "string",
              },
              {
                name: "status",
                customName: "pagerduty_status",
                type: "string",
              },
            ],
          },
        },
        420,
      ),

      // 6. AI response drafter
      aiNode(
        "6",
        "AI: Draft crisis response",
        1240,
        "You are a crisis communications expert. Draft an immediate, empathetic, and professional response to the negative mention. Keep it brief and acknowledge the concern. Return JSON.",
        "Crisis mention from {mention_author} on {mention_platform}: {mention_text}. Severity: {mention_severity}. Topics: {mention_topics}. Recommended action: {recommended_action}. Draft a crisis response. Return draft_response (string), response_tone (string), escalation_notes (string), suggested_channels (string comma-separated channels to respond on).",
        [
          {
            field: "draft_response",
            as: "crisis_response_draft",
            type: "string",
          },
          { field: "response_tone", as: "response_tone", type: "string" },
          { field: "escalation_notes", as: "escalation_notes", type: "string" },
          {
            field: "suggested_channels",
            as: "suggested_channels",
            type: "string",
          },
        ],
        420,
      ),

      // 7. Standard alert emailer (non-crisis path)
      emailNode(
        "7",
        "Send standard mention alert email",
        1000,
        "{brand_manager_email}",
        "Social Mention Alert: {mention_sentiment} mention on {mention_platform}",
        "Social Mention Alert\n\nPlatform: {mention_platform}\nAuthor: {mention_author}\nSentiment: {mention_sentiment}\nSeverity: {mention_severity}\nVirality Risk: {virality_risk}%\n\nMention:\n{mention_text}\n\nTopics: {mention_topics}\nRecommended Action: {recommended_action}\nResponse Urgency: {response_urgency}\n\nSERP Context: {serp_context}",
      ),

      // 8. Positive mention harvester
      n("8", "runCode", "Harvest positive mentions for marketing", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Harvest positive mentions for social proof and marketing use
const sentiment = vars.mention_sentiment || "";
const text = vars.mention_text || "";
const author = vars.mention_author || "";
const platform = vars.mention_platform || "";
const isPositive = sentiment === "positive";
const isUsable = isPositive && text.length >= 30 && !text.includes("sponsored");
return {
  is_positive_mention: isPositive ? "true" : "false",
  is_marketing_usable: isUsable ? "true" : "false",
  harvested_quote: isUsable ? text : "",
  harvest_author: isUsable ? author : "",
  harvest_platform: isUsable ? platform : "",
  harvest_status: isUsable ? "harvested" : "skipped"
};`,
          outputVariables: [
            { name: "is_positive_mention", type: "string" },
            { name: "is_marketing_usable", type: "string" },
            { name: "harvested_quote", type: "string" },
            { name: "harvest_author", type: "string" },
            { name: "harvest_platform", type: "string" },
            { name: "harvest_status", type: "string" },
          ],
        },
      }),

      // 9. Trend analytics logger (Sheets)
      sheetsNode(
        "9",
        "Log mention to trend analytics Sheets",
        1480,
        "Sentiment_Trends",
        `[["${new Date().toISOString()}","{mention_platform}","{mention_author}","{mention_sentiment}","{mention_severity}","{virality_risk}","{response_urgency}","{mention_topics}","{harvest_status}","{recommended_action}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // crisis -> PagerDuty + draft response
      e("4", "7", "output_2"), // non-crisis -> standard email
      e("5", "6"),
      e("6", "9"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1197. SOURCE-SPECIFIC WELCOME EVENT SIGNUP (8 nodes)
  // ===================================================================

  "Source-Specific Welcome Event Signup": {
    summary:
      "Listens for event signups, enriches with event metadata, deduplicates contacts in HubSpot, composes an AI-personalized event welcome, sends the welcome email, delivers resources after a delay, creates a CRM record, and enrolls the contact in a nurture sequence.",
    nodes: [
      trigger("Webhook: New event signup received", "webhook_app", {
        source: "event_platform",
        event: "registration.created",
        howItStarts:
          "A new event registration is completed, triggering a personalized welcome sequence tailored to the specific event",
        setup:
          "Connect your event platform (Eventbrite, Hopin, or custom). Configure HubSpot for contact management. Set up OpenAI for personalized welcome composition.",
        inputFields: [],
      }),

      // 2. Event metadata enricher
      aiNode(
        "2",
        "AI: Extract signup data and enrich with event metadata",
        280,
        "You are a registration data processor. Extract the registrant's details and event information from the webhook payload. Return JSON.",
        "Extract event signup data from: {webhook_payload}. Find the registrant email, name, company, event name, event date, ticket type, and any special requests regardless of format. Return registrant_email (string), registrant_name (string), registrant_company (string), event_name (string), event_date (string), ticket_type (string), special_requests (string), event_description (string).",
        [
          { field: "registrant_email", as: "registrant_email", type: "string" },
          { field: "registrant_name", as: "registrant_name", type: "string" },
          {
            field: "registrant_company",
            as: "registrant_company",
            type: "string",
          },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "ticket_type", as: "ticket_type", type: "string" },
          { field: "special_requests", as: "special_requests", type: "string" },
          {
            field: "event_description",
            as: "event_description",
            type: "string",
          },
        ],
      ),

      // 3. Contact dedup check (HubSpot)
      n("3", "hitApi", "Check for existing contact in HubSpot", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{registrant_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "existing_contact_count",
              type: "number",
            },
            {
              name: "results[0].id",
              customName: "existing_hubspot_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI event welcome composer
      aiNode(
        "4",
        "AI: Compose personalized event welcome",
        760,
        "You are an event communications specialist. Create a warm, event-specific welcome message that includes key event details, what to expect, and any preparation tips. Return JSON.",
        "Compose a welcome email for {registrant_name} from {registrant_company} who registered for {event_name} on {event_date}. Ticket type: {ticket_type}. Special requests: {special_requests}. Event description: {event_description}. Return welcome_subject (string), welcome_body (string with event details, agenda highlights, and preparation tips), preparation_checklist (string comma-separated items), networking_tip (string).",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          {
            field: "preparation_checklist",
            as: "preparation_checklist",
            type: "string",
          },
          { field: "networking_tip", as: "networking_tip", type: "string" },
        ],
      ),

      // 5. Welcome email sender
      emailNode(
        "5",
        "Send personalized event welcome email",
        1000,
        "{registrant_email}",
        "{welcome_subject}",
        "{welcome_body}\n\nPreparation Checklist:\n{preparation_checklist}\n\nNetworking Tip: {networking_tip}\n\nWe look forward to seeing you at {event_name}!\n\nBest,\nThe Event Team",
      ),

      // 6. Resource delivery (delay)
      n("6", "delayAction", "Wait 1 day before sending event resources", 1240, {
        delayAction: { time: 1, timeFormat: "days" },
      }),

      // 7. CRM record creator
      hubspotCreateContact("7", "Create or update HubSpot contact", 1480, {
        email: "{registrant_email}",
        firstname: "{registrant_name}",
        company: "{registrant_company}",
        event_registered: "{event_name}",
        event_date: "{event_date}",
        ticket_type: "{ticket_type}",
        lead_source: "event_signup",
        lifecycle_stage: "lead",
      }),

      // 8. Nurture enrollment
      n("8", "hitApi", "Enroll registrant in event nurture sequence", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/automation/v4/flows/{event_nurture_workflow_id}/enrollments",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "contactId", value: "{hubspot_contact_id}" },
            { key: "enrollmentSource", value: "event_signup_{event_name}" },
          ],
          variables: [
            {
              name: "status",
              customName: "nurture_enrollment_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1198. SOURCE-SPECIFIC WELCOME ORGANIC SIGNUP (7 nodes)
  // ===================================================================

  "Source-Specific Welcome Organic Signup": {
    summary:
      "Detects organic signups via webhook, fetches landing page context with code, composes an AI educational welcome tailored to the organic source, sends the welcome email, follows up after a delay with trust-building content, creates a CRM record in HubSpot, and enrolls in an educational nurture.",
    nodes: [
      trigger("Webhook: New organic signup detected", "webhook_app", {
        source: "website",
        event: "signup.organic",
        howItStarts:
          "A new user signs up through an organic channel (search engine, direct visit, or blog referral), triggering an educational welcome sequence",
        setup:
          "Configure your website/app to send signup webhooks with referrer data. Set up OpenAI for personalized welcome composition and HubSpot for CRM management.",
        inputFields: [],
      }),

      // 2. Landing page context fetcher (runCode)
      n("2", "runCode", "Extract signup source and landing page context", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract organic signup details and landing page context
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.subscriber_email || payload.user_email || "";
const name = payload.name || payload.first_name || payload.fullName || "";
const referrer = payload.referrer || payload.utm_source || payload.source || "direct";
const landingPage = payload.landing_page || payload.page_url || payload.url || "/";
const searchQuery = payload.search_query || payload.keyword || "";
const isSearchTraffic = referrer.includes("google") || referrer.includes("bing") || referrer.includes("yahoo") || searchQuery.length > 0;
const isBlogReferral = landingPage.includes("/blog") || landingPage.includes("/article");
const contentTopic = isBlogReferral ? landingPage.split("/").pop().replace(/-/g, " ") : "";
return {
  subscriber_email: email,
  subscriber_name: name || "there",
  referrer_source: referrer,
  landing_page: landingPage,
  search_query: searchQuery,
  is_search_traffic: isSearchTraffic ? "true" : "false",
  is_blog_referral: isBlogReferral ? "true" : "false",
  content_topic: contentTopic,
  signup_source_category: isSearchTraffic ? "search" : isBlogReferral ? "blog" : "direct"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "referrer_source", type: "string" },
            { name: "landing_page", type: "string" },
            { name: "search_query", type: "string" },
            { name: "is_search_traffic", type: "string" },
            { name: "is_blog_referral", type: "string" },
            { name: "content_topic", type: "string" },
            { name: "signup_source_category", type: "string" },
          ],
        },
      }),

      // 3. AI educational welcome composer
      aiNode(
        "3",
        "AI: Compose educational welcome tailored to organic source",
        520,
        "You are an educational content strategist. Create a welcome email that matches the subscriber's organic journey — if they came from search, reference their query; if from a blog, continue the educational thread; if direct, provide a comprehensive intro. Return JSON.",
        "Compose a welcome email for {subscriber_name} ({subscriber_email}). Source: {signup_source_category}. Referrer: {referrer_source}. Landing page: {landing_page}. Search query: {search_query}. Blog topic: {content_topic}. Return email_subject (string), email_body (string educational and value-driven), recommended_resources (string comma-separated links/topics), learning_path_suggestion (string), personalization_note (string explaining why this content was chosen).",
        [
          { field: "email_subject", as: "welcome_subject", type: "string" },
          { field: "email_body", as: "welcome_body", type: "string" },
          {
            field: "recommended_resources",
            as: "recommended_resources",
            type: "string",
          },
          {
            field: "learning_path_suggestion",
            as: "learning_path",
            type: "string",
          },
          {
            field: "personalization_note",
            as: "personalization_note",
            type: "string",
          },
        ],
      ),

      // 4. Welcome email sender
      emailNode(
        "4",
        "Send educational welcome email",
        760,
        "{subscriber_email}",
        "{welcome_subject}",
        "Hi {subscriber_name},\n\n{welcome_body}\n\nRecommended Resources: {recommended_resources}\n\nSuggested Learning Path: {learning_path}\n\n{personalization_note}\n\nBest,\nThe Team",
      ),

      // 5. Trust-building follow-up (delay)
      n(
        "5",
        "delayAction",
        "Wait 2 days before trust-building follow-up",
        1000,
        {
          delayAction: { time: 2, timeFormat: "days" },
        },
      ),

      // 6. CRM record creator (HubSpot)
      hubspotCreateContact(
        "6",
        "Create HubSpot contact for organic subscriber",
        1240,
        {
          email: "{subscriber_email}",
          firstname: "{subscriber_name}",
          lead_source: "organic_{signup_source_category}",
          original_source: "{referrer_source}",
          landing_page: "{landing_page}",
          search_query: "{search_query}",
          lifecycle_stage: "subscriber",
        },
      ),

      // 7. Educational nurture enrollor
      n("7", "hitApi", "Enroll in educational nurture sequence", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/automation/v4/flows/{educational_nurture_id}/enrollments",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "contactId", value: "{hubspot_contact_id}" },
            {
              key: "enrollmentSource",
              value: "organic_{signup_source_category}",
            },
          ],
          variables: [
            { name: "status", customName: "nurture_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1199. SOURCE-SPECIFIC WELCOME PAID SOCIAL (8 nodes)
  // ===================================================================

  "Source-Specific Welcome Paid Social": {
    summary:
      "Detects paid social signups, fetches campaign data with code, reinforces the ad's promise via AI, sends a welcome email, nudges toward quick conversion after a delay, boosts with social proof, creates a HubSpot CRM record, and fires an ad platform conversion signal.",
    nodes: [
      trigger("Webhook: Paid social signup detected", "webhook_app", {
        source: "ad_platform",
        event: "signup.paid_social",
        howItStarts:
          "A new user signs up from a paid social media ad, triggering a conversion-optimized welcome sequence that reinforces the ad's promise",
        setup:
          "Configure your ad platforms to send signup webhooks with campaign data. Set up OpenAI for ad promise reinforcement and HubSpot for CRM management.",
        inputFields: [],
      }),

      // 2. Campaign data fetcher (runCode)
      n("2", "runCode", "Extract signup and campaign data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract paid social signup data and campaign details
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.subscriber_email || "";
const name = payload.name || payload.first_name || "";
const platform = payload.utm_source || payload.platform || payload.source || "social";
const campaign = payload.utm_campaign || payload.campaign_name || "";
const adContent = payload.utm_content || payload.ad_creative || "";
const adGroup = payload.utm_term || payload.ad_group || "";
const offerPromise = payload.offer || payload.ad_offer || payload.promo || "";
const clickCost = payload.cpc || payload.cost_per_click || "0";
return {
  subscriber_email: email,
  subscriber_name: name || "there",
  ad_platform: platform,
  campaign_name: campaign,
  ad_content: adContent,
  ad_group: adGroup,
  original_offer: offerPromise,
  click_cost: clickCost,
  signup_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "ad_platform", type: "string" },
            { name: "campaign_name", type: "string" },
            { name: "ad_content", type: "string" },
            { name: "ad_group", type: "string" },
            { name: "original_offer", type: "string" },
            { name: "click_cost", type: "string" },
            { name: "signup_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI ad promise reinforcer
      aiNode(
        "3",
        "AI: Reinforce ad promise in welcome message",
        520,
        "You are a conversion copywriter. Create a welcome email that directly reinforces the promise made in the ad, creating continuity from ad click to inbox. Make the subscriber feel they made the right decision. Return JSON.",
        "Create a welcome email for {subscriber_name} who clicked a {ad_platform} ad. Campaign: {campaign_name}. Ad content/promise: {ad_content}. Original offer: {original_offer}. Reinforce the ad promise and drive toward the first conversion. Return email_subject (string), email_body (string reinforcing ad promise), urgency_element (string), trust_signal (string), primary_cta (string), cta_url (string).",
        [
          { field: "email_subject", as: "welcome_subject", type: "string" },
          { field: "email_body", as: "welcome_body", type: "string" },
          { field: "urgency_element", as: "urgency_element", type: "string" },
          { field: "trust_signal", as: "trust_signal", type: "string" },
          { field: "primary_cta", as: "primary_cta", type: "string" },
          { field: "cta_url", as: "cta_url", type: "string" },
        ],
      ),

      // 4. Welcome email sender
      emailNode(
        "4",
        "Send ad-promise welcome email",
        760,
        "{subscriber_email}",
        "{welcome_subject}",
        "Hi {subscriber_name},\n\n{welcome_body}\n\n{urgency_element}\n\n{trust_signal}\n\n{primary_cta}\n\nBest,\nThe Team",
      ),

      // 5. Quick conversion nudge (delay)
      n("5", "delayAction", "Wait 4 hours before conversion nudge", 1000, {
        delayAction: { time: 4, timeFormat: "hours" },
      }),

      // 6. Social proof booster
      emailNode(
        "6",
        "Send social proof conversion booster",
        1240,
        "{subscriber_email}",
        "See why others chose us, {subscriber_name}",
        "Hi {subscriber_name},\n\nYou're in great company! Here's what others who found us through {ad_platform} have to say:\n\n{trust_signal}\n\nDon't miss out on your offer: {original_offer}\n\n{urgency_element}\n\nTake action now: {cta_url}\n\nBest,\nThe Team",
      ),

      // 7. CRM record creator (HubSpot)
      hubspotCreateContact(
        "7",
        "Create HubSpot contact for paid social lead",
        1480,
        {
          email: "{subscriber_email}",
          firstname: "{subscriber_name}",
          lead_source: "paid_social",
          ad_platform: "{ad_platform}",
          campaign: "{campaign_name}",
          ad_content: "{ad_content}",
          original_offer: "{original_offer}",
          lifecycle_stage: "lead",
        },
      ),

      // 8. Ad platform conversion signal
      n("8", "hitApi", "Fire conversion event to ad platform", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{pixel_id}/events",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Authorization", value: "Bearer {social_access_token}" },
          ],
          body: [
            {
              key: "data",
              value:
                '[{"event_name":"Lead","event_time":"{signup_timestamp}","user_data":{"em":"{subscriber_email}"},"custom_data":{"campaign":"{campaign_name}","value":"{click_cost}"}}]',
            },
            { key: "access_token", value: "{social_access_token}" },
          ],
          variables: [
            {
              name: "events_received",
              customName: "conversion_events_sent",
              type: "number",
            },
            {
              name: "fbtrace_id",
              customName: "conversion_trace_id",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1200. SOURCE-SPECIFIC WELCOME QR OR IN-STORE SIGNUP (8 nodes)
  // ===================================================================

  "Source-Specific Welcome QR or In-Store Signup": {
    summary:
      "Detects signups from QR codes or in-store kiosks, enriches with store/location data via code, routes by source type, sends QR-specific or in-store-specific welcome emails, delivers via the chosen channel, creates a HubSpot CRM record, and builds an omnichannel profile.",
    nodes: [
      trigger("Webhook: QR code or in-store signup detected", "webhook_app", {
        source: "retail_platform",
        event: "signup.physical",
        howItStarts:
          "A new customer signs up via a QR code scan or an in-store kiosk, triggering a source-specific welcome sequence",
        setup:
          "Configure your QR code platform and in-store kiosk system to send signup webhooks. Set up HubSpot for CRM management and OpenAI for personalized welcomes.",
        inputFields: [],
      }),

      // 2. Store/location enricher (runCode)
      n(
        "2",
        "runCode",
        "Extract signup data and enrich with location context",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Extract physical signup data and determine source type
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.subscriber_email || "";
const name = payload.name || payload.first_name || "";
const phone = payload.phone || payload.mobile || "";
const source = payload.source || payload.signup_source || "";
const storeId = payload.store_id || payload.location_id || "";
const storeName = payload.store_name || payload.location_name || "our store";
const qrCampaign = payload.qr_campaign || payload.campaign || "";
const isQr = source.toLowerCase().includes("qr") || qrCampaign.length > 0;
const isInStore = source.toLowerCase().includes("store") || source.toLowerCase().includes("kiosk") || storeId.length > 0;
return {
  subscriber_email: email,
  subscriber_name: name || "there",
  subscriber_phone: phone,
  signup_source: isQr ? "qr_code" : isInStore ? "in_store" : "physical_other",
  store_id: storeId,
  store_name: storeName,
  qr_campaign: qrCampaign,
  is_qr_signup: isQr ? "true" : "false",
  is_instore_signup: isInStore ? "true" : "false"
};`,
            outputVariables: [
              { name: "subscriber_email", type: "string" },
              { name: "subscriber_name", type: "string" },
              { name: "subscriber_phone", type: "string" },
              { name: "signup_source", type: "string" },
              { name: "store_id", type: "string" },
              { name: "store_name", type: "string" },
              { name: "qr_campaign", type: "string" },
              { name: "is_qr_signup", type: "string" },
              { name: "is_instore_signup", type: "string" },
            ],
          },
        },
      ),

      // 3. Source path router condition
      n("3", "condition", "Is this a QR code signup?", 520, {
        conditionData: [
          {
            variable: "is_qr_signup",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. QR welcome email
      emailNode(
        "4",
        "Send QR-specific welcome email",
        760,
        "{subscriber_email}",
        "Thanks for scanning! Welcome aboard, {subscriber_name}",
        "Hi {subscriber_name},\n\nThanks for scanning our QR code{qr_campaign ? ' from our ' + qr_campaign + ' campaign' : ''}! We're thrilled to connect with you.\n\nAs a special thank you for going digital with us, here's an exclusive offer just for QR scanners.\n\nExplore what we have in store for you and enjoy a seamless experience across all our channels.\n\nBest,\nThe Team",
        420,
      ),

      // 5. In-store welcome email
      emailNode(
        "5",
        "Send in-store welcome email",
        760,
        "{subscriber_email}",
        "Great meeting you at {store_name}, {subscriber_name}!",
        "Hi {subscriber_name},\n\nIt was wonderful meeting you at {store_name}! Thank you for joining our community.\n\nNow you can enjoy the best of both worlds — our in-store experience plus exclusive online perks.\n\nHere's a special welcome offer to use on your next visit or online.\n\nWe look forward to seeing you again!\n\nBest,\nThe {store_name} Team",
      ),

      // 6. Welcome email sender (merged path — sends resource follow-up)
      emailNode(
        "6",
        "Send resource follow-up email",
        1000,
        "{subscriber_email}",
        "Your personalized guide is ready, {subscriber_name}",
        "Hi {subscriber_name},\n\nWhether you found us via QR or in-store at {store_name}, we want to make sure you get the most out of your experience.\n\nHere are some resources tailored just for you:\n- Browse our latest collection online\n- Download our app for exclusive in-store perks\n- Check your nearest store hours and events\n\nEnjoy!\n\nBest,\nThe Team",
      ),

      // 7. CRM record creator (HubSpot)
      hubspotCreateContact(
        "7",
        "Create HubSpot contact for physical signup",
        1240,
        {
          email: "{subscriber_email}",
          firstname: "{subscriber_name}",
          phone: "{subscriber_phone}",
          lead_source: "{signup_source}",
          store_id: "{store_id}",
          store_name: "{store_name}",
          qr_campaign: "{qr_campaign}",
          lifecycle_stage: "lead",
        },
      ),

      // 8. Omnichannel profile builder
      n("8", "runCode", "Build omnichannel customer profile", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build an omnichannel profile combining physical and digital touchpoints
const email = vars.subscriber_email || "";
const name = vars.subscriber_name || "";
const phone = vars.subscriber_phone || "";
const source = vars.signup_source || "";
const storeId = vars.store_id || "";
const storeName = vars.store_name || "";
const hubspotId = vars.hubspot_contact_id || "";
const profile = {
  email: email,
  name: name,
  phone: phone,
  first_touchpoint: source,
  physical_store: storeName,
  store_id: storeId,
  crm_id: hubspotId,
  channels: [source === "qr_code" ? "qr" : "in_store", "email"],
  profile_completeness: [email, name, phone, storeId].filter(v => v.length > 0).length * 25
};
return {
  omni_profile_json: JSON.stringify(profile),
  profile_completeness_pct: profile.profile_completeness,
  channels_connected: profile.channels.join(","),
  profile_status: profile.profile_completeness >= 75 ? "complete" : "partial"
};`,
          outputVariables: [
            { name: "omni_profile_json", type: "string" },
            { name: "profile_completeness_pct", type: "number" },
            { name: "channels_connected", type: "string" },
            { name: "profile_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // QR signup -> QR welcome
      e("3", "5", "output_2"), // in-store signup -> in-store welcome
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1201. SOURCE-SPECIFIC WELCOME REFERRAL OR AFFILIATE (8 nodes)
  // ===================================================================

  "Source-Specific Welcome Referral or Affiliate": {
    summary:
      "Detects referral/affiliate signups, identifies the referrer or affiliate with code, calculates dual incentives, sends a welcome email to the new subscriber, notifies the referrer, logs affiliate commissions to Sheets, updates both records in HubSpot, and enrolls the referred subscriber in nurture.",
    nodes: [
      trigger("Webhook: Referral or affiliate signup detected", "webhook_app", {
        source: "referral_platform",
        event: "signup.referral",
        howItStarts:
          "A new user signs up through a referral link or affiliate program, triggering a dual-reward welcome sequence for both parties",
        setup:
          "Connect your referral/affiliate platform to send signup webhooks. Configure HubSpot for CRM management and Sheets for commission tracking.",
        inputFields: [],
      }),

      // 2. Referrer/affiliate identifier (runCode)
      n(
        "2",
        "runCode",
        "Identify referrer or affiliate and extract signup data",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Extract referral/affiliate signup data and identify the referrer
const payload = JSON.parse(vars.webhook_payload || "{}");
const newEmail = payload.email || payload.subscriber_email || "";
const newName = payload.name || payload.first_name || "";
const referralCode = payload.referral_code || payload.ref || payload.affiliate_id || "";
const referrerEmail = payload.referrer_email || payload.advocate_email || "";
const referrerName = payload.referrer_name || payload.advocate_name || "";
const isAffiliate = payload.is_affiliate === true || payload.type === "affiliate" || referralCode.startsWith("AFF");
const channel = isAffiliate ? "affiliate" : "referral";
const programTier = isAffiliate ? (payload.affiliate_tier || "standard") : "referral";
return {
  new_subscriber_email: newEmail,
  new_subscriber_name: newName || "there",
  referral_code: referralCode,
  referrer_email: referrerEmail,
  referrer_name: referrerName || "Your friend",
  is_affiliate: isAffiliate ? "true" : "false",
  referral_channel: channel,
  program_tier: programTier,
  signup_date: new Date().toISOString()
};`,
            outputVariables: [
              { name: "new_subscriber_email", type: "string" },
              { name: "new_subscriber_name", type: "string" },
              { name: "referral_code", type: "string" },
              { name: "referrer_email", type: "string" },
              { name: "referrer_name", type: "string" },
              { name: "is_affiliate", type: "string" },
              { name: "referral_channel", type: "string" },
              { name: "program_tier", type: "string" },
              { name: "signup_date", type: "string" },
            ],
          },
        },
      ),

      // 3. Dual incentive calculator
      n("3", "runCode", "Calculate incentives for both parties", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate incentives for both the new subscriber and the referrer/affiliate
const channel = vars.referral_channel || "referral";
const tier = vars.program_tier || "standard";
let newSubscriberReward = "";
let referrerReward = "";
let affiliateCommission = 0;
if (channel === "affiliate") {
  const commissionRates = { standard: 10, silver: 15, gold: 20, platinum: 25 };
  affiliateCommission = commissionRates[tier] || 10;
  newSubscriberReward = "15% off your first purchase";
  referrerReward = affiliateCommission + "% commission on this referral";
} else {
  newSubscriberReward = "20% off your first purchase";
  referrerReward = "$10 credit added to your account";
  affiliateCommission = 0;
}
const newCode = "REF-NEW-" + Math.random().toString(36).substring(2, 8).toUpperCase();
const referrerCode = "REF-THX-" + Math.random().toString(36).substring(2, 8).toUpperCase();
return {
  new_subscriber_reward: newSubscriberReward,
  new_subscriber_code: newCode,
  referrer_reward: referrerReward,
  referrer_code: referrerCode,
  affiliate_commission_pct: affiliateCommission,
  incentive_type: channel === "affiliate" ? "commission" : "dual_reward"
};`,
          outputVariables: [
            { name: "new_subscriber_reward", type: "string" },
            { name: "new_subscriber_code", type: "string" },
            { name: "referrer_reward", type: "string" },
            { name: "referrer_code", type: "string" },
            { name: "affiliate_commission_pct", type: "number" },
            { name: "incentive_type", type: "string" },
          ],
        },
      }),

      // 4. New subscriber welcome email
      emailNode(
        "4",
        "Send welcome email to new subscriber",
        760,
        "{new_subscriber_email}",
        "Welcome! {referrer_name} thought you'd love this",
        "Hi {new_subscriber_name},\n\n{referrer_name} referred you to us, and we're so glad you're here!\n\nAs a welcome gift, enjoy {new_subscriber_reward} with code: {new_subscriber_code}\n\nWe can't wait for you to experience what our community is all about.\n\nBest,\nThe Team",
      ),

      // 5. Referrer notification email
      emailNode(
        "5",
        "Notify referrer of successful signup",
        1000,
        "{referrer_email}",
        "Your referral {new_subscriber_name} just joined!",
        "Hi {referrer_name},\n\nGreat news! {new_subscriber_name} just signed up using your referral.\n\nYour reward: {referrer_reward}\nReward code: {referrer_code}\n\nKeep sharing — every referral earns you more rewards!\n\nThank you for being an amazing advocate.\n\nBest,\nThe Team",
      ),

      // 6. Affiliate commission logger (Sheets)
      sheetsNode(
        "6",
        "Log referral/affiliate commission to Sheets",
        1240,
        "Referral_Commissions",
        `[["${new Date().toISOString()}","{referral_channel}","{referral_code}","{referrer_email}","{referrer_name}","{new_subscriber_email}","{new_subscriber_name}","{affiliate_commission_pct}","{referrer_reward}","{program_tier}"]]`,
      ),

      // 7. CRM dual record update (HubSpot)
      hubspotCreateContact(
        "7",
        "Create HubSpot contact for referred subscriber",
        1480,
        {
          email: "{new_subscriber_email}",
          firstname: "{new_subscriber_name}",
          lead_source: "{referral_channel}",
          referral_code: "{referral_code}",
          referred_by: "{referrer_email}",
          program_tier: "{program_tier}",
          lifecycle_stage: "lead",
        },
      ),

      // 8. Referred subscriber nurture
      n("8", "hitApi", "Enroll referred subscriber in nurture sequence", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/automation/v4/flows/{referral_nurture_id}/enrollments",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "contactId", value: "{hubspot_contact_id}" },
            { key: "enrollmentSource", value: "{referral_channel}_signup" },
          ],
          variables: [
            {
              name: "status",
              customName: "nurture_enrollment_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1202. SPIN-TO-WIN CAPTURE (8 nodes)
  // ===================================================================

  "Spin-to-Win Capture": {
    summary:
      "Triggers a spin-to-win widget, captures email/SMS opt-in, randomizes the prize with code, reveals the prize via in-app message, generates a unique prize code, delivers via email and SMS, and creates a CRM record in HubSpot.",
    nodes: [
      trigger("Webhook: Spin-to-win widget interaction", "webhook_app", {
        source: "website_widget",
        event: "spin.initiated",
        howItStarts:
          "A visitor interacts with the spin-to-win widget on the website, providing their email and/or phone number to play",
        setup:
          "Deploy the spin-to-win widget on your website. Configure Twilio for SMS delivery and HubSpot for CRM record creation.",
        inputFields: [],
      }),

      // 2. Email/SMS capture
      aiNode(
        "2",
        "AI: Extract visitor capture data",
        280,
        "You are a data extraction specialist. Extract the visitor's email, phone number, and any consent flags from the spin widget submission. Return JSON.",
        "Extract visitor data from the spin-to-win submission: {webhook_payload}. Find the visitor email, phone number, SMS consent, and email consent regardless of format. Return visitor_email (string), visitor_phone (string), visitor_name (string), sms_consent (boolean), email_consent (boolean), page_url (string the page where they spun).",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_phone", as: "visitor_phone", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "sms_consent", as: "sms_consent", type: "boolean" },
          { field: "email_consent", as: "email_consent", type: "boolean" },
          { field: "page_url", as: "page_url", type: "string" },
        ],
      ),

      // 3. Prize randomizer (runCode)
      n("3", "runCode", "Randomize prize from weighted prize pool", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Weighted random prize selection from the prize pool
const prizes = [
  { name: "10% Off", weight: 40, code_prefix: "SPIN10" },
  { name: "15% Off", weight: 25, code_prefix: "SPIN15" },
  { name: "20% Off", weight: 15, code_prefix: "SPIN20" },
  { name: "Free Shipping", weight: 12, code_prefix: "SHIPFREE" },
  { name: "25% Off", weight: 5, code_prefix: "SPIN25" },
  { name: "50% Off", weight: 2, code_prefix: "SPIN50" },
  { name: "Free Gift", weight: 1, code_prefix: "GIFT" }
];
const totalWeight = prizes.reduce((sum, p) => sum + p.weight, 0);
let random = Math.random() * totalWeight;
let selectedPrize = prizes[0];
for (const prize of prizes) {
  random -= prize.weight;
  if (random <= 0) { selectedPrize = prize; break; }
}
const uniqueCode = selectedPrize.code_prefix + "-" + Math.random().toString(36).substring(2, 8).toUpperCase();
return {
  prize_name: selectedPrize.name,
  prize_code: uniqueCode,
  prize_code_prefix: selectedPrize.code_prefix,
  prize_rarity: selectedPrize.weight <= 5 ? "rare" : selectedPrize.weight <= 15 ? "uncommon" : "common",
  prize_expiry_days: 7,
  spin_result_message: "Congratulations! You won " + selectedPrize.name + "!"
};`,
          outputVariables: [
            { name: "prize_name", type: "string" },
            { name: "prize_code", type: "string" },
            { name: "prize_code_prefix", type: "string" },
            { name: "prize_rarity", type: "string" },
            { name: "prize_expiry_days", type: "number" },
            { name: "spin_result_message", type: "string" },
          ],
        },
      }),

      // 4. Spin animation + reveal (sendMessage)
      n("4", "sendMessage", "Display spin result to visitor", 760, {
        sendMessageDetails: {
          channel: "in_app",
          message:
            "{spin_result_message}\n\nYour prize: {prize_name}\nCode: {prize_code}\nExpires in {prize_expiry_days} days\n\nCheck your email and phone for the code!",
        },
      }),

      // 5. Prize code generator (unique code already generated in step 3, this validates)
      n("5", "runCode", "Validate and finalize prize code", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate prize code and set expiry date
const code = vars.prize_code || "";
const expiryDays = parseInt(vars.prize_expiry_days) || 7;
const expiryDate = new Date();
expiryDate.setDate(expiryDate.getDate() + expiryDays);
return {
  final_prize_code: code,
  prize_expiry_date: expiryDate.toISOString().split("T")[0],
  code_valid: code.length > 0 ? "true" : "false",
  prize_delivery_channels: (vars.email_consent === "true" ? "email" : "") + (vars.sms_consent === "true" ? ",sms" : "")
};`,
          outputVariables: [
            { name: "final_prize_code", type: "string" },
            { name: "prize_expiry_date", type: "string" },
            { name: "code_valid", type: "string" },
            { name: "prize_delivery_channels", type: "string" },
          ],
        },
      }),

      // 6. Prize delivery email
      emailNode(
        "6",
        "Send prize code via email",
        1240,
        "{visitor_email}",
        "You won {prize_name}! Here's your code",
        "Hi {visitor_name},\n\nCongratulations on your spin-to-win prize!\n\nYour Prize: {prize_name}\nYour Code: {final_prize_code}\nExpires: {prize_expiry_date}\n\nUse this code at checkout to claim your reward.\n\nHappy shopping!\n\nBest,\nThe Team",
      ),

      // 7. Prize delivery SMS (Twilio)
      smsNode(
        "7",
        "Send prize code via SMS (Twilio)",
        1480,
        "{visitor_phone}",
        "You won {prize_name}! Use code {final_prize_code} at checkout. Expires {prize_expiry_date}. Reply STOP to opt out.",
      ),

      // 8. CRM record creator (HubSpot)
      hubspotCreateContact(
        "8",
        "Create HubSpot contact for spin-to-win lead",
        1720,
        {
          email: "{visitor_email}",
          firstname: "{visitor_name}",
          phone: "{visitor_phone}",
          lead_source: "spin_to_win",
          prize_won: "{prize_name}",
          prize_code: "{final_prize_code}",
          prize_expiry: "{prize_expiry_date}",
          sms_consent: "{sms_consent}",
          lifecycle_stage: "lead",
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1203. STACKEXCHANGE ANSWER DRAFT (8 nodes)
  // ===================================================================

  "StackExchange Answer Draft": {
    summary:
      "Monitors StackExchange for relevant questions, scores relevance with code, routes on quality threshold, drafts an AI answer, checks for self-promotion, gates on technical accuracy review, posts the approved answer, and logs performance to Sheets.",
    nodes: [
      trigger("Webhook: New StackExchange question detected", "webhook_app", {
        source: "stackexchange",
        event: "question.created",
        howItStarts:
          "A new question matching your topic tags is posted on StackExchange, triggering the answer drafting pipeline",
        setup:
          "Connect StackExchange API for question monitoring. Configure OpenAI for answer drafting. Set up an approval gate for technical review and Sheets for performance tracking.",
        inputFields: [],
      }),

      // 2. Relevance scorer (runCode)
      n("2", "runCode", "Score question relevance to your expertise", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score question relevance based on tags, keywords, and topic match
const payload = JSON.parse(vars.webhook_payload || "{}");
const title = payload.title || payload.question_title || "";
const body = payload.body || payload.question_body || "";
const tags = (payload.tags || []).join(",");
const viewCount = parseInt(payload.view_count) || 0;
const answerCount = parseInt(payload.answer_count) || 0;
const questionId = payload.question_id || payload.id || "";
const expertiseTags = ["javascript","typescript","react","node.js","python","api","rest","graphql"];
const tagMatches = expertiseTags.filter(t => tags.toLowerCase().includes(t)).length;
const keywordScore = expertiseTags.filter(t => (title + " " + body).toLowerCase().includes(t)).length;
const relevanceScore = (tagMatches * 20) + (keywordScore * 10) + (viewCount > 100 ? 15 : 0) + (answerCount === 0 ? 20 : 0);
return {
  question_id: questionId,
  question_title: title,
  question_body: body.substring(0, 2000),
  question_tags: tags,
  view_count: viewCount,
  answer_count: answerCount,
  relevance_score: Math.min(relevanceScore, 100),
  tag_matches: tagMatches,
  is_unanswered: answerCount === 0 ? "true" : "false"
};`,
          outputVariables: [
            { name: "question_id", type: "string" },
            { name: "question_title", type: "string" },
            { name: "question_body", type: "string" },
            { name: "question_tags", type: "string" },
            { name: "view_count", type: "number" },
            { name: "answer_count", type: "number" },
            { name: "relevance_score", type: "number" },
            { name: "tag_matches", type: "number" },
            { name: "is_unanswered", type: "string" },
          ],
        },
      }),

      // 3. Quality threshold condition
      n("3", "condition", "Is the question relevant enough to answer?", 520, {
        conditionData: [
          {
            variable: "relevance_score",
            value: "40",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI answer drafter
      aiNode(
        "4",
        "AI: Draft technical StackExchange answer",
        760,
        "You are a senior software engineer writing a StackExchange answer. Be thorough, include code examples, explain your reasoning, and follow StackExchange best practices. Avoid self-promotion. Return JSON.",
        "Draft an answer for this StackExchange question. Title: {question_title}. Tags: {question_tags}. Body: {question_body}. The answer should be technically accurate, well-formatted with code examples, and helpful. Return answer_text (string in markdown), code_example (string), answer_confidence (number 0-100), key_concepts (string comma-separated), estimated_helpfulness (number 0-10).",
        [
          { field: "answer_text", as: "draft_answer", type: "string" },
          { field: "code_example", as: "code_example", type: "string" },
          {
            field: "answer_confidence",
            as: "answer_confidence",
            type: "number",
          },
          { field: "key_concepts", as: "key_concepts", type: "string" },
          {
            field: "estimated_helpfulness",
            as: "estimated_helpfulness",
            type: "number",
          },
        ],
      ),

      // 5. Self-promotion checker
      n("5", "runCode", "Check answer for self-promotion violations", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check the draft answer for self-promotion, spam, or policy violations
const answer = vars.draft_answer || "";
const code = vars.code_example || "";
const fullText = answer + " " + code;
const selfPromoPatterns = [
  /\\b(our product|our service|my company|check out our|visit our)\\b/i,
  /\\b(buy|purchase|subscribe|sign up for our)\\b/i,
  /https?:\\/\\/(?!stackoverflow|github|mdn|w3schools|developer\\.mozilla)/i
];
const violations = [];
selfPromoPatterns.forEach((pattern, idx) => {
  if (pattern.test(fullText)) violations.push("self_promo_pattern_" + idx);
});
const wordCount = fullText.split(/\\s+/).length;
const hasCodeBlock = fullText.includes("\`\`\`") || code.length > 10;
return {
  promo_violations_json: JSON.stringify(violations),
  promo_violation_count: violations.length,
  is_clean: violations.length === 0 ? "true" : "false",
  answer_word_count: wordCount,
  has_code_example: hasCodeBlock ? "true" : "false",
  quality_assessment: hasCodeBlock && wordCount > 50 && violations.length === 0 ? "high" : "needs_review"
};`,
          outputVariables: [
            { name: "promo_violations_json", type: "string" },
            { name: "promo_violation_count", type: "number" },
            { name: "is_clean", type: "string" },
            { name: "answer_word_count", type: "number" },
            { name: "has_code_example", type: "string" },
            { name: "quality_assessment", type: "string" },
          ],
        },
      }),

      // 6. Technical accuracy reviewer (approval gate)
      n(
        "6",
        "approvalGate",
        "Technical review: Approve answer before posting",
        1240,
        {
          approvalGateData: {
            message:
              "StackExchange Answer Review\n\nQuestion: {question_title}\nTags: {question_tags}\nRelevance: {relevance_score}/100\nViews: {view_count} | Existing Answers: {answer_count}\n\nDraft Answer ({answer_word_count} words):\n{draft_answer}\n\nCode Example:\n{code_example}\n\nConfidence: {answer_confidence}%\nHelpfulness: {estimated_helpfulness}/10\nSelf-Promo Check: {is_clean} ({promo_violation_count} violations)\nQuality: {quality_assessment}\n\nApprove to post, reject to discard.",
          },
        },
      ),

      // 7. StackExchange answer poster
      n("7", "hitApi", "Post answer to StackExchange", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stackexchange.com/2.3/questions/{question_id}/answers/add",
          headers: [
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "body", value: "{draft_answer}" },
            { key: "key", value: "{stackexchange_api_key}" },
            { key: "access_token", value: "{stackexchange_token}" },
            { key: "site", value: "stackoverflow" },
          ],
          variables: [
            {
              name: "items[0].answer_id",
              customName: "posted_answer_id",
              type: "number",
            },
            {
              name: "items[0].score",
              customName: "answer_score",
              type: "number",
            },
          ],
        },
      }),

      // 8. Performance logger (Sheets)
      sheetsNode(
        "8",
        "Log answer to performance tracker",
        1720,
        "SE_Answers",
        `[["${new Date().toISOString()}","{question_id}","{question_title}","{question_tags}","{relevance_score}","{answer_confidence}","{quality_assessment}","{posted_answer_id}","{answer_word_count}","{key_concepts}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // relevant -> draft answer
      e("3", "8", "output_2"), // not relevant -> log and skip
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1204. STANDARD EMAIL WELCOME SERIES (7 nodes)
  // ===================================================================

  "Standard Email Welcome Series": {
    summary:
      "Triggers on new subscriber signup, sends an instant welcome email, waits and checks engagement, sends a brand deep dive, follows up with social proof and CTA, reminds about expiring welcome offers, and logs series completion to Sheets.",
    nodes: [
      trigger("Webhook: New email subscriber added", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber joins the email list, triggering a multi-touch welcome series designed to convert and engage",
        setup:
          "Configure your email platform to send subscriber webhooks. Set up Sheets for completion tracking.",
        inputFields: [],
      }),

      // 2. Email 1: Instant welcome
      aiNode(
        "2",
        "AI: Extract subscriber data and compose welcome",
        280,
        "You are an email marketing specialist. Extract subscriber details and compose a warm, engaging welcome email. Return JSON.",
        "Extract subscriber data from: {webhook_payload}. Find the subscriber email, name, source, and any interests regardless of format. Return subscriber_email (string), subscriber_name (string), signup_source (string), interests (string), welcome_subject (string), welcome_body (string warm welcome with brand intro and value proposition), welcome_offer_code (string generated 6-char discount code).",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "interests", as: "interests", type: "string" },
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          {
            field: "welcome_offer_code",
            as: "welcome_offer_code",
            type: "string",
          },
        ],
      ),

      // 2b. Send welcome email
      emailNode(
        "2b",
        "Send Email 1: Instant welcome",
        400,
        "{subscriber_email}",
        "{welcome_subject}",
        "Hi {subscriber_name},\n\n{welcome_body}\n\nUse code {welcome_offer_code} for your welcome offer.\n\nBest,\nThe Team",
      ),

      // 3. Engagement check (delay + condition)
      n("3", "delayAction", "Wait 2 days to check engagement", 640, {
        delayAction: { time: 2, timeFormat: "days" },
      }),

      // 4. Email 2: Brand deep dive
      emailNode(
        "4",
        "Send Email 2: Brand story deep dive",
        880,
        "{subscriber_email}",
        "The story behind what we do, {subscriber_name}",
        "Hi {subscriber_name},\n\nWe wanted to share a bit more about who we are and why we do what we do.\n\nOur mission is to help people like you achieve their goals. Here's how we're different:\n\n1. We believe in quality over quantity\n2. Every customer is part of our community\n3. We're constantly innovating based on your feedback\n\nHave questions? Just reply to this email — a real person will get back to you.\n\nBest,\nThe Team",
      ),

      // 5. Email 3: Social proof + CTA
      emailNode(
        "5",
        "Send Email 3: Social proof and CTA",
        1120,
        "{subscriber_email}",
        "See what {subscriber_name}'s peers are saying",
        'Hi {subscriber_name},\n\nDon\'t just take our word for it — here\'s what our community has to say:\n\n"This changed how I work." — Sarah K.\n"Best decision I made this year." — Michael R.\n"The quality is unmatched." — Emma T.\n\nReady to join them? Your welcome offer ({welcome_offer_code}) is still active.\n\nTake the next step today.\n\nBest,\nThe Team',
      ),

      // 6. Welcome offer expiry reminder
      emailNode(
        "6",
        "Send welcome offer expiry reminder",
        1360,
        "{subscriber_email}",
        "Your welcome offer expires soon, {subscriber_name}!",
        "Hi {subscriber_name},\n\nJust a friendly reminder — your welcome offer is expiring soon!\n\nCode: {welcome_offer_code}\n\nDon't miss out on this exclusive new-member benefit. After it expires, it's gone for good.\n\nClaim your offer now.\n\nBest,\nThe Team",
      ),

      // 7. Series completion logger (Sheets)
      sheetsNode(
        "7",
        "Log welcome series completion to Sheets",
        1600,
        "Welcome_Series",
        `[["${new Date().toISOString()}","{subscriber_email}","{subscriber_name}","{signup_source}","{interests}","completed","3_emails_sent","{welcome_offer_code}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1205. STANDARD SMS WELCOME (6 nodes)
  // ===================================================================

  "Standard SMS Welcome": {
    summary:
      "Triggers on SMS opt-in, sends a Twilio welcome SMS, waits for engagement, sends a day-2 nudge SMS, processes opt-outs with code, and logs the subscriber to HubSpot CRM.",
    nodes: [
      trigger("Webhook: SMS opt-in received", "webhook_app", {
        source: "sms_platform",
        event: "sms.optin",
        howItStarts:
          "A new subscriber opts in to receive SMS messages, triggering an automated welcome and engagement sequence via Twilio",
        setup:
          "Configure your SMS opt-in form or keyword trigger to send webhooks. Set up Twilio for SMS delivery and HubSpot for CRM logging.",
        inputFields: [],
      }),

      // 2. Welcome SMS sender (Twilio)
      aiNode(
        "1b",
        "AI: Extract opt-in data",
        200,
        "You are an SMS data processor. Extract the subscriber's phone number, name, and opt-in source. Return JSON.",
        "Extract SMS opt-in data from: {webhook_payload}. Find the phone number, subscriber name, opt-in keyword, and source regardless of format. Return phone_number (string), subscriber_name (string), optin_keyword (string), optin_source (string).",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "optin_keyword", as: "optin_keyword", type: "string" },
          { field: "optin_source", as: "optin_source", type: "string" },
        ],
      ),

      smsNode(
        "2",
        "Send welcome SMS via Twilio",
        400,
        "{phone_number}",
        "Welcome to our SMS club, {subscriber_name}! You'll get exclusive deals and updates. Reply HELP for help or STOP to unsubscribe. Msg&data rates may apply.",
      ),

      // 3. Engagement wait (delay)
      n("3", "delayAction", "Wait 1 day for engagement window", 640, {
        delayAction: { time: 1, timeFormat: "days" },
      }),

      // 4. Day-2 nudge SMS
      smsNode(
        "4",
        "Send day-2 engagement nudge SMS",
        880,
        "{phone_number}",
        "Hey {subscriber_name}! As a VIP SMS subscriber, here's an exclusive offer just for you. Check it out: {sms_offer_link}. Reply STOP to opt out.",
      ),

      // 5. Opt-out handler (runCode)
      n("5", "runCode", "Process opt-out requests and update status", 1120, {
        runCodeDetails: {
          language: "javascript",
          code: `// Handle opt-out processing and status management
const phone = vars.phone_number || "";
const name = vars.subscriber_name || "";
const keyword = vars.optin_keyword || "";
const source = vars.optin_source || "";
const optedOut = false; // Would check against opt-out database in production
return {
  final_phone: phone,
  final_name: name,
  optin_keyword: keyword,
  optin_source: source,
  opted_out: optedOut ? "true" : "false",
  subscriber_status: optedOut ? "unsubscribed" : "active",
  sms_messages_sent: 2,
  sequence_completed: "true"
};`,
          outputVariables: [
            { name: "final_phone", type: "string" },
            { name: "final_name", type: "string" },
            { name: "optin_keyword", type: "string" },
            { name: "optin_source", type: "string" },
            { name: "opted_out", type: "string" },
            { name: "subscriber_status", type: "string" },
            { name: "sms_messages_sent", type: "number" },
            { name: "sequence_completed", type: "string" },
          ],
        },
      }),

      // 6. CRM logger (HubSpot)
      hubspotCreateContact("6", "Log SMS subscriber to HubSpot", 1360, {
        phone: "{final_phone}",
        firstname: "{final_name}",
        lead_source: "sms_optin",
        sms_optin_keyword: "{optin_keyword}",
        sms_optin_source: "{optin_source}",
        sms_status: "{subscriber_status}",
        lifecycle_stage: "subscriber",
      }),
    ],
    edges: [
      e("1", "1b"),
      e("1b", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 1206. STANDARD WINBACK EMAIL (8 nodes)
  // ===================================================================

  "Standard Winback Email": {
    summary:
      "Scans for lapsed customers on schedule, filters exclusions with code, sends a 'we miss you' email, waits and checks engagement, escalates with a stronger offer, delivers a last-chance email, tracks win-back conversions, and logs outcomes to Sheets.",
    nodes: [
      trigger("Schedule: Weekly lapsed customer winback scan", "schedule", {
        schedule: "0 10 * * 1 (every Monday at 10 AM)",
        howItStarts:
          "A weekly scan identifies customers who haven't engaged in 60+ days, triggering a multi-stage winback email campaign",
        setup:
          "Configure your customer database or CRM to identify lapsed customers. Set up Sheets for outcome logging.",
        inputFields: [],
      }),

      // 2. Exclusion filter (runCode)
      n(
        "2",
        "runCode",
        "Filter exclusions and prepare lapsed customer data",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Extract lapsed customer data and filter exclusions
const payload = JSON.parse(vars.webhook_payload || "{}");
const customers = payload.lapsed_customers || [];
const customer = customers[0] || payload;
const email = customer.email || customer.subscriber_email || "";
const name = customer.name || customer.first_name || "";
const lastPurchase = customer.last_purchase_date || customer.last_active || "";
const totalSpend = parseFloat(customer.total_spend || customer.lifetime_value || "0");
const purchaseCount = parseInt(customer.purchase_count || customer.order_count || "0");
const isExcluded = customer.unsubscribed === true || customer.do_not_contact === true || email === "";
const daysSinceActive = lastPurchase ? Math.floor((Date.now() - new Date(lastPurchase).getTime()) / (1000 * 60 * 60 * 24)) : 999;
const winbackTier = totalSpend > 500 ? "vip" : totalSpend > 100 ? "standard" : "basic";
return {
  customer_email: email,
  customer_name: name || "there",
  last_purchase_date: lastPurchase,
  days_since_active: daysSinceActive,
  total_spend: totalSpend,
  purchase_count: purchaseCount,
  is_excluded: isExcluded ? "true" : "false",
  winback_tier: winbackTier,
  offer_amount: winbackTier === "vip" ? "25%" : winbackTier === "standard" ? "20%" : "15%"
};`,
            outputVariables: [
              { name: "customer_email", type: "string" },
              { name: "customer_name", type: "string" },
              { name: "last_purchase_date", type: "string" },
              { name: "days_since_active", type: "number" },
              { name: "total_spend", type: "number" },
              { name: "purchase_count", type: "number" },
              { name: "is_excluded", type: "string" },
              { name: "winback_tier", type: "string" },
              { name: "offer_amount", type: "string" },
            ],
          },
        },
      ),

      // 3. Email 1: We miss you
      emailNode(
        "3",
        "Send Email 1: We miss you",
        520,
        "{customer_email}",
        "We miss you, {customer_name}!",
        "Hi {customer_name},\n\nWe noticed it's been a while since your last visit ({days_since_active} days to be exact).\n\nWe've been busy making things even better, and we'd love for you to come back and see what's new.\n\nAs a thank you for being a loyal customer with {purchase_count} purchases, here's {offer_amount} off your next order.\n\nWe hope to see you soon!\n\nBest,\nThe Team",
      ),

      // 4. Engagement check (delay + condition)
      n("4", "delayAction", "Wait 3 days for engagement", 760, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 4b. Check engagement condition
      n("4b", "condition", "Did customer re-engage after Email 1?", 920, {
        conditionData: [
          {
            variable: "days_since_active",
            value: "60",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Email 2: Escalated offer (still lapsed path)
      emailNode(
        "5",
        "Send Email 2: Escalated offer",
        1120,
        "{customer_email}",
        "We've sweetened the deal for you, {customer_name}",
        "Hi {customer_name},\n\nWe really want you back, so we're making our offer even better.\n\nAs a {winback_tier} customer who's spent ${total_spend} with us, you deserve something special:\n\nGet {offer_amount} off PLUS free shipping on your next order.\n\nThis upgraded offer won't last long — claim it before it's gone.\n\nBest,\nThe Team",
      ),

      // 6. Email 3: Last chance
      emailNode(
        "6",
        "Send Email 3: Last chance",
        1360,
        "{customer_email}",
        "Last chance: Your special offer expires tomorrow, {customer_name}",
        "Hi {customer_name},\n\nThis is your final reminder — your exclusive {offer_amount} off + free shipping offer expires tomorrow.\n\nWe'd hate for you to miss out. After this, the offer is gone.\n\nClaim your offer now before it's too late.\n\nWe truly value your business.\n\nBest,\nThe Team",
      ),

      // 7. Win-back conversion tracker + outcome logger (Sheets)
      sheetsNode(
        "7",
        "Log winback outcome to Sheets",
        1600,
        "Winback_Outcomes",
        `[["${new Date().toISOString()}","{customer_email}","{customer_name}","{winback_tier}","{days_since_active}","{total_spend}","{purchase_count}","{offer_amount}","3_emails_sent","pending_conversion"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "5", "output_1"), // still lapsed -> escalated offer
      e("4b", "7", "output_2"), // re-engaged -> log success
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1207. STANDARD WINBACK SMS (7 nodes)
  // ===================================================================

  "Standard Winback SMS": {
    summary:
      "Scans for lapsed SMS subscribers on schedule, assigns offer tiers with code, sends re-engagement SMS via Twilio, waits and monitors response, sends a final nudge SMS, processes opt-outs, and logs winback outcomes to Sheets.",
    nodes: [
      trigger("Schedule: Weekly lapsed SMS subscriber winback", "schedule", {
        schedule: "0 11 * * 2 (every Tuesday at 11 AM)",
        howItStarts:
          "A weekly scan identifies SMS subscribers who haven't engaged in 45+ days, triggering an SMS re-engagement sequence via Twilio",
        setup:
          "Configure your SMS subscriber database to identify lapsed subscribers. Set up Twilio for SMS delivery and Sheets for outcome tracking.",
        inputFields: [],
      }),

      // 2. Offer tier assigner (runCode)
      n("2", "runCode", "Assign offer tier based on subscriber value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract lapsed SMS subscriber data and assign offer tier
const payload = JSON.parse(vars.webhook_payload || "{}");
const phone = payload.phone || payload.phone_number || payload.mobile || "";
const name = payload.name || payload.subscriber_name || "";
const lastActive = payload.last_active || payload.last_engagement || "";
const totalOrders = parseInt(payload.total_orders || payload.order_count || "0");
const daysSinceActive = lastActive ? Math.floor((Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24)) : 999;
const subscriberValue = totalOrders > 10 ? "high" : totalOrders > 3 ? "medium" : "low";
const offerMap = { high: "30% off", medium: "20% off", low: "15% off" };
const offer = offerMap[subscriberValue] || "15% off";
const offerCode = "WINBACK-" + Math.random().toString(36).substring(2, 8).toUpperCase();
return {
  subscriber_phone: phone,
  subscriber_name: name || "there",
  last_active_date: lastActive,
  days_since_active: daysSinceActive,
  total_orders: totalOrders,
  subscriber_value: subscriberValue,
  winback_offer: offer,
  winback_code: offerCode,
  subscriber_status: "lapsed"
};`,
          outputVariables: [
            { name: "subscriber_phone", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "last_active_date", type: "string" },
            { name: "days_since_active", type: "number" },
            { name: "total_orders", type: "number" },
            { name: "subscriber_value", type: "string" },
            { name: "winback_offer", type: "string" },
            { name: "winback_code", type: "string" },
            { name: "subscriber_status", type: "string" },
          ],
        },
      }),

      // 3. SMS 1: Re-engagement (Twilio)
      smsNode(
        "3",
        "Send SMS 1: Re-engagement offer via Twilio",
        520,
        "{subscriber_phone}",
        "Hey {subscriber_name}! We miss you. Here's {winback_offer} just for you. Use code {winback_code} at checkout. Offer expires in 5 days. Reply STOP to opt out.",
      ),

      // 4. Response monitor (delay + condition)
      n("4", "delayAction", "Wait 3 days for response", 760, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 4b. Engagement condition
      n("4b", "condition", "Did subscriber respond or engage?", 920, {
        conditionData: [
          {
            variable: "subscriber_status",
            value: "lapsed",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. SMS 2: Final nudge (still lapsed path)
      smsNode(
        "5",
        "Send SMS 2: Final nudge via Twilio",
        1120,
        "{subscriber_phone}",
        "Last chance, {subscriber_name}! Your {winback_offer} code {winback_code} expires tomorrow. Don't miss out! Reply STOP to opt out.",
      ),

      // 6. Opt-out processor
      n("6", "runCode", "Process opt-outs and finalize status", 1360, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process any opt-out responses and finalize subscriber status
const phone = vars.subscriber_phone || "";
const name = vars.subscriber_name || "";
const value = vars.subscriber_value || "low";
const daysSince = parseInt(vars.days_since_active) || 0;
const orders = parseInt(vars.total_orders) || 0;
const offer = vars.winback_offer || "";
const code = vars.winback_code || "";
// In production, check opt-out database for STOP responses
const optedOut = false;
return {
  final_status: optedOut ? "opted_out" : "winback_pending",
  opted_out: optedOut ? "true" : "false",
  sms_sent_count: 2,
  winback_outcome: optedOut ? "opted_out" : "pending_conversion",
  subscriber_phone_final: phone,
  subscriber_name_final: name,
  subscriber_value_final: value,
  days_lapsed: daysSince,
  order_history: orders,
  offer_used: offer,
  code_used: code
};`,
          outputVariables: [
            { name: "final_status", type: "string" },
            { name: "opted_out", type: "string" },
            { name: "sms_sent_count", type: "number" },
            { name: "winback_outcome", type: "string" },
            { name: "subscriber_phone_final", type: "string" },
            { name: "subscriber_name_final", type: "string" },
            { name: "subscriber_value_final", type: "string" },
            { name: "days_lapsed", type: "number" },
            { name: "order_history", type: "number" },
            { name: "offer_used", type: "string" },
            { name: "code_used", type: "string" },
          ],
        },
      }),

      // 7. Winback outcome logger (Sheets)
      sheetsNode(
        "7",
        "Log SMS winback outcome to Sheets",
        1600,
        "SMS_Winback_Outcomes",
        `[["${new Date().toISOString()}","{subscriber_phone_final}","{subscriber_name_final}","{subscriber_value_final}","{days_lapsed}","{order_history}","{offer_used}","{code_used}","{sms_sent_count}","{winback_outcome}","{final_status}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "5", "output_1"), // still lapsed -> final nudge
      e("4b", "7", "output_2"), // re-engaged -> log and exit
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
